import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

// ── CityInput avec Nominatim + GPS automatique ──────────────────
function CityInput({ value, onChange, inputStyle }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);  

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=6&addressdetails=1`,
          { headers: { 'Accept-Language': 'fr' } }
        );
        const data = await res.json();
        const results = data
          .filter(d => d.class === 'place' || d.class === 'boundary' || ['city','town','village','municipality','administrative'].includes(d.addresstype) || ['city','town','village'].includes(d.type))
          .map(d => ({
  name: d.address?.city || d.address?.town || d.address?.village || d.address?.municipality || d.address?.hamlet || d.name || '',
  region: d.address?.state || d.address?.county || d.address?.region || '',
  country: d.address?.country || '',
  countryCode: d.address?.country_code || 'fr',
  lat: parseFloat(d.lat),
  lng: parseFloat(d.lon),
}))
          .filter((s, i, arr) => s.name && arr.findIndex(x => x.name === s.name) === i)
          .slice(0, 6);
        setSuggestions(results);
      } catch(e) { setSuggestions([]); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const select = (item) => {
  setQuery(item.name);
  onChange(item.name, { lat: item.lat, lng: item.lng, countryCode: item.countryCode, region: item.region, country: item.country });
  setSuggestions([]);
};

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input style={inputStyle} placeholder="Rechercher une ville..." value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value, null); }}
        onFocus={e => e.target.style.borderColor = 'var(--green)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
        autoComplete="off" />
      {suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 9999, overflow: 'hidden', marginTop: 4 }}>
          {suggestions.map((item, i) => (
            <div key={i} onMouseDown={() => select(item)}
              style={{ padding: '10px 14px', fontSize: 14, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '0.5px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--green-light)'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ────────────────────────────────────────────────────────────────

const CameraIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>;
const PlusIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const HomeIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21V9l9-6 9 6v12"/><path d="M9 21V13h6v8"/></svg>;
const SofaIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="8" width="20" height="10" rx="2"/><path d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2"/><line x1="2" y1="14" x2="22" y2="14"/></svg>;
const BedIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 20v-6a2 2 0 012-2h16a2 2 0 012 2v6"/><path d="M2 14v-4a2 2 0 012-2h16a2 2 0 012 2v4"/></svg>;
const KitchenIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><circle cx="7" cy="7" r="1"/><circle cx="12" cy="7" r="1"/></svg>;
const BathIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 12h16v4a4 4 0 01-4 4H8a4 4 0 01-4-4v-4z"/><path d="M4 12V6a2 2 0 014 0v1"/></svg>;
const SunIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>;
const Bed2Icon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 20v-6a2 2 0 012-2h16a2 2 0 012 2v6"/><path d="M2 14v-4a2 2 0 012-2h16a2 2 0 012 2v4"/></svg>;
const StarIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const WifiIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><circle cx="12" cy="20" r="1"/></svg>;
const CarIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 17H3a2 2 0 01-2-2V9a2 2 0 012-2h14l4 4v4a2 2 0 01-2 2h-2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>;
const PoolIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M2 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><circle cx="12" cy="6" r="2"/><path d="M12 8v5"/></svg>;
const SnowIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 7l-5 5-5-5"/><path d="M17 17l-5-5-5 5"/><line x1="2" y1="12" x2="22" y2="12"/></svg>;
const WashIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="13" r="4"/><circle cx="7" cy="7" r="1"/></svg>;
const CoffeeIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>;
const TvIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="20" height="14" rx="2"/><line x1="8" y1="22" x2="16" y2="22"/><line x1="12" y1="18" x2="12" y2="22"/></svg>;
const FlameIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 01-7 7 7 7 0 01-7-7c0-1.507.332-2.585.5-3.5.75.5 1.5 1.5 2 2z"/></svg>;
const BikeIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6a1 1 0 100-2 1 1 0 000 2zm-3 11.5V14l-3-3 4-3 2 3h2"/></svg>;
const PawIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="8" r="2"/><circle cx="17" cy="8" r="2"/><circle cx="4" cy="13" r="2"/><circle cx="20" cy="13" r="2"/><path d="M12 20c-4 0-6-2-6-4 0-3 2-4 6-4s6 1 6 4c0 2-2 4-6 4z"/></svg>;
const BabyIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="7" r="3"/><path d="M6 20v-2a6 6 0 0112 0v2"/></svg>;
const DropIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z"/></svg>;

const PHOTO_SLOTS = [
  { id: 1, label: 'Exterieur / façade',       tip: 'Photo de jour, logement entier visible',             required: true,  Icon: HomeIcon    },
  { id: 2, label: 'Salon / séjour',            tip: 'pièce principale, bien éclairé',                   required: true,  Icon: SofaIcon    },
  { id: 3, label: 'Chambre principale',        tip: 'Lit fait, rangements visibles',                     required: true,  Icon: BedIcon     },
  { id: 4, label: 'Cuisine',                   tip: 'Plan de travail dégagé, Équipements visibles',      required: true,  Icon: KitchenIcon },
  { id: 5, label: 'Salle de bain',             tip: 'Propre, serviettes pliées si possible',             required: true,  Icon: BathIcon    },
  { id: 6, label: 'Vue / terrasse / jardin',   tip: 'Le grand atout de votre logement',                  required: false, Icon: SunIcon     },
  { id: 7, label: 'Chambre 2 (si applicable)', tip: 'Même style que la chambre principale',              required: false, Icon: Bed2Icon    },
  { id: 8, label: 'Detail / ambiance',         tip: 'Ce qui rend votre logement unique',                 required: false, Icon: StarIcon    },
];

const AMENITIES_LIST = [
  { id: 'wifi',      label: 'WiFi',              Icon: WifiIcon   },
  { id: 'parking',   label: 'Parking',           Icon: CarIcon    },
  { id: 'piscine',   label: 'Piscine',           Icon: PoolIcon   },
  { id: 'clim',      label: 'Climatisation',     Icon: SnowIcon   },
  { id: 'lave',      label: 'Lave-linge',        Icon: WashIcon   },
  { id: 'cuisine',   label: 'cuisine equipée',   Icon: CoffeeIcon },
  { id: 'tv',        label: 'TV',                Icon: TvIcon     },
  { id: 'bbq',       label: 'Barbecue',          Icon: FlameIcon  },
  { id: 'velo',      label: 'Vélos',             Icon: BikeIcon   },
  { id: 'animaux',   label: 'Animaux acceptés',  Icon: PawIcon    },
  { id: 'bébé',      label: 'Équipement bébé',   Icon: BabyIcon   },
  { id: 'jacuzzi',   label: 'Jacuzzi',           Icon: DropIcon   },
  { id: 'lingerie',  label: 'Linge fourni',      Icon: BedIcon    },
];

const catégorieS = [
  { id: 'mer',      label: 'Mer',      emoji: 'plage' },
  { id: 'montagne', label: 'Montagne', emoji: 'ski' },
  { id: 'campagne', label: 'Campagne', emoji: 'nature' },
  { id: 'ville',    label: 'Ville',    emoji: 'urbain' },
  { id: 'lac',      label: 'Lac',      emoji: 'lac' },
  { id: 'luxe',     label: 'Luxe',     emoji: 'prestige' },
];

const TITLE_SUGGESTIONS = {
  Villa:       city => `Villa avec piscine et terrasse vue mer - ${city}`,
  Appartement: city => `Appartement lumineux en plein centre de ${city}`,
  Maison:      city => `Maison de charme avec jardin privatif - ${city}`,
  Chalet:      city => `Chalet montagnard avec jacuzzi et vue panoramique - ${city}`,
  Studio:      city => `Studio cosy idealement situe a ${city}`,
  Chambre:     city => `Chambre privee dans maison d'hotes - ${city}`,
  Loft:        city => `Loft industriel et design au coeur de ${city}`,
  Mas:         city => `Mas provencal authentique avec oliveraie - ${city}`,
};
const PRICE_RANGES = {
  Villa: '150 à 500 / nuit', Appartement: '60 à 150 / nuit',
  Maison: '80 à 200 / nuit', Chalet: '120 à 350 / nuit',
  Studio: '40 à 90 / nuit',  Chambre: '30 à 70 / nuit',
  Loft: '80 à 180 / nuit',   Mas: '150 à 450 / nuit',
};
const getTitleSuggestion = (type, city) => TITLE_SUGGESTIONS[type] ? TITLE_SUGGESTIONS[type](city) : `${type} a ${city}`;
const getPriceRange = (type) => PRICE_RANGES[type] || '60 a 200 / nuit';

const SEASONS = {
  haute:   { label: 'Haute saison',   months: 'Juillet · Août · Vacances scolaires', color: 'var(--terra-mid)', bg: 'var(--terra-light)', advice: 'Samedi -> Samedi', adviceMin: '7 nuits minimum', reason: 'Les rotations hebdomadaires maximisent vos revenus et simplifient la logistique.' },
  moyenne: { label: 'Moyenne saison', months: 'Avril · Mai · Juin · Septembre · Octobre', color: '#534AB7', bg: '#EEEDFE', advice: 'Vendredi -> Lundi ou 4 nuits min.', adviceMin: '4 nuits minimum', reason: 'En moyenne saison, les courts séjours de week-end sont fréquents.' },
  basse:   { label: 'Basse saison',   months: 'Novembre · Décembre · Janvier · Février · Mars', color: 'var(--green-mid)', bg: 'var(--green-light)', advice: 'Flexible - 3 nuits minimum', adviceMin: '3 nuits minimum', reason: "En basse saison, la flexibilité est clé pour maintenir un bon taux d'occupation." },
};

const BASE_STEPS = ['Emma IA', 'Photos', 'Logement', 'Équipements', 'Tarifs', 'Règles', 'Guide local', 'Documents', 'Recapitulatif'];
const MANAGER_STEPS = ['propriétaire', 'Emma IA', 'Photos', 'Logement', 'Équipements', 'Tarifs', 'Règles', 'Guide local', 'Documents', 'Recapitulatif'];

function calcScore(form) {
  let score = 0;
  if (form.photos.filter(Boolean).length >= 5) score += 25;
  if (form.description?.length >= 100) score += 20;
  if (form.amenities?.length >= 5) score += 15;
  if (form.registrationNumber) score += 20;
  if (form.rules?.length >= 50) score += 10;
  if (form.caution > 0) score += 10;
  return score;
}

// ═══════════════════════════════════════════════════════════════════════
// 1. AJOUTE CE COMPOSANT juste avant "export default function NewListingPage"
// 2. REMPLACE le bloc {!isMobile && !isEdit && ( ... )} par <ListingPreview ... />
// ═══════════════════════════════════════════════════════════════════════

// ─── COMPOSANT PREVIEW ────────────────────────────────────────────────
function ListingPreview({ form, photoPreviews, customAmenities, transparencyScore, videoUrl, step, stepOffset }) {
  const [carouselIdx, setCarouselIdx] = useState(0);

  const previewPhotos = photoPreviews.filter(Boolean);

  useEffect(() => {
    if (carouselIdx >= previewPhotos.length && previewPhotos.length > 0) {
      setCarouselIdx(previewPhotos.length - 1);
    }
  }, [previewPhotos.length]);

  const amenityLabels = (form.amenities || [])
    .map(id => AMENITIES_LIST.find(a => a.id === id)?.label)
    .filter(Boolean);
  const allAmenities = [...amenityLabels, ...(customAmenities || [])];

  const showAmenities = step >= 2 + stepOffset && allAmenities.length > 0;
  const showPrice     = step >= 3 + stepOffset && form.priceNight;
  const showRules     = step >= 4 + stepOffset;
  const showGuide     = step >= 5 + stepOffset;
  const showFull      = step >= 6 + stepOffset;

  const rulesAll = [
    ...(form.smokingAllowed ? [] : ['Non-fumeur']),
    ...(form.petsAllowed ? ['Animaux acceptés'] : ['Animaux non acceptés']),
    ...(form.partiesAllowed ? ['Evènements autorisés'] : ['Pas de fêtes']),
    ...(form.minNights > 1 ? [`Minimum ${form.minNights} nuits`] : []),
    ...((form.extraRules || []).filter(Boolean)),
  ];

  const totalGuests = (form.adults || 0) + (form.children || 0);

  return (
    <div style={{ position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

        {/* Barre titre fenetree */}
        <div style={{ background: 'var(--surface)', padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF5F57' }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFBD2E' }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#28CA41' }} />
          <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 8 }}>Apercu voyageur — temps reel</span>
        </div>

        {/* ── CARROUSEL ── */}
        <div style={{ height: 200, background: 'var(--surface)', position: 'relative', overflow: 'hidden' }}>
          {previewPhotos.length > 0 ? (
            <>
              <img
                src={previewPhotos[carouselIdx]}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.2s' }}
              />
              {previewPhotos.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIdx(i => (i - 1 + previewPhotos.length) % previewPhotos.length)}
                    style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ‹
                  </button>
                  <button
                    onClick={() => setCarouselIdx(i => (i + 1) % previewPhotos.length)}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ›
                  </button>
                  {/* Compteur */}
                  <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, zIndex: 2 }}>
                    {carouselIdx + 1} / {previewPhotos.length}
                  </div>
                  {/* Vignettes */}
                  <div style={{ position: 'absolute', bottom: 6, left: 0, right: 0, display: 'flex', gap: 3, padding: '0 8px', overflowX: 'auto', zIndex: 2 }}>
                    {previewPhotos.map((src, i) => (
                      <div key={i} onClick={() => setCarouselIdx(i)}
                        style={{ width: 36, height: 26, borderRadius: 4, overflow: 'hidden', flexShrink: 0, cursor: 'pointer', border: `2px solid ${i === carouselIdx ? '#fff' : 'transparent'}`, transition: 'border 0.15s' }}>
                        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--ink-4)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <span style={{ fontSize: 11 }}>Vos photos apparaitront ici</span>
            </div>
          )}
          {/* Badge score */}
          {transparencyScore > 0 && (
            <div style={{ position: 'absolute', top: 10, right: 10, background: transparencyScore >= 80 ? 'var(--green)' : transparencyScore >= 50 ? '#534AB7' : '#C4784A', color: '#fff', fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, zIndex: 2 }}>
              ✓ {transparencyScore}/100
            </div>
          )}
        </div>

        {/* ── CONTENU ── */}
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Titre + meta */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4, lineHeight: 1.3 }}>
              {form.title || <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>Titre de votre annonce</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              {[form.type, form.category, form.city].filter(Boolean).join(' · ') || <span style={{ fontStyle: 'italic' }}>Type · Ville</span>}
            </div>
            {form.proximity && <div style={{ fontSize: 11, color: 'var(--green-mid)', marginTop: 3 }}>📍 {form.proximity}</div>}
            {form.target_audience && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                {form.target_audience.split(',').filter(Boolean).map((t, i) => (
                  <span key={i} style={{ fontSize: 10, padding: '2px 7px', background: '#FDF8F4', color: '#C4784A', borderRadius: 20 }}>🎯 {t.trim()}</span>
                ))}
              </div>
            )}
          </div>

          {/* Prix */}
          {showPrice && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 22, fontWeight: 600 }}>{form.priceNight} €</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>/ nuit · prix final</span>
              {form.cleaningFee > 0 && <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>+ {form.cleaningFee} € ménage</span>}
            </div>
          )}

          {/* Capacite */}
          {totalGuests > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {[
                `👤 ${form.adults} adulte${form.adults > 1 ? 's' : ''}`,
                form.children > 0 && `🧒 ${form.children} enfant${form.children > 1 ? 's' : ''}`,
                form.beds > 0 && `🛏 ${form.beds} chambre${form.beds > 1 ? 's' : ''}`,
                form.baths > 0 && `🚿 ${form.baths} sdb`,
              ].filter(Boolean).map((t, i) => (
                <span key={i} style={{ fontSize: 11, background: 'var(--surface)', padding: '3px 8px', borderRadius: 20, color: 'var(--ink-3)' }}>{t}</span>
              ))}
            </div>
          )}

          {/* Description */}
          {form.description && (
            <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, margin: 0, display: '-webkit-box', WebkitLineClamp: showFull ? 999 : 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {form.description}
            </p>
          )}

          {/* Équipements */}
          {showAmenities && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Équipements</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {allAmenities.map((label, i) => (
                  <span key={i} style={{ fontSize: 10, padding: '3px 8px', background: 'var(--green-light)', color: 'var(--green-mid)', borderRadius: 20, fontWeight: 500 }}>{label}</span>
                ))}
              </div>
            </div>
          )}

          {/* Règles */}
          {showRules && rulesAll.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Règles</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {form.checkinTime  && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>🕐 Check-in à partir de {form.checkinTime}</div>}
                {form.checkoutTime && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>🕐 Check-out avant {form.checkoutTime}</div>}
                {rulesAll.map((r, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', gap: 5 }}>
                    <span style={{ color: 'var(--ink-4)' }}>·</span><span>{r}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Guide local */}
          {showGuide && (form.localGuide?.restaurants || form.localGuide?.plages || form.localGuide?.activités || form.localGuide?.transports) && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>Guide local</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { key: 'restaurants', icon: '🍽', label: 'Restaurants' },
                  { key: 'plages',      icon: '🏖', label: 'Nature & Plages' },
                  { key: 'activités',   icon: '🎯', label: 'Activités' },
                  { key: 'transports',  icon: '🚌', label: 'Transports' },
                ].filter(f => form.localGuide?.[f.key]).map(f => (
                  <div key={f.key} style={{ background: 'var(--surface)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink)', marginBottom: 2 }}>{f.icon} {f.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {form.localGuide[f.key]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          {showFull && form.faq?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>FAQ voyageurs ({form.faq.length} reponses)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.faq.map((f, i) => (
                  <div key={i} style={{ background: '#EEEDFE', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#3C3489', marginBottom: 2 }}>Q : {f.q}</div>
                    <div style={{ fontSize: 11, color: '#534AB7', lineHeight: 1.6 }}>{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Badges bas */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
            {form.caution > 0 && <span style={{ fontSize: 10, padding: '3px 8px', background: '#E8F5F0', color: '#0F6E56', borderRadius: 20 }}>🔒 Protection {form.caution} €</span>}
            {form.localGuide?.restaurants && <span style={{ fontSize: 10, padding: '3px 8px', background: '#EEEDFE', color: '#534AB7', borderRadius: 20 }}>🗺 Guide local</span>}
            {form.faq?.length > 0 && <span style={{ fontSize: 10, padding: '3px 8px', background: '#FDF0E6', color: '#C4784A', borderRadius: 20 }}>🤖 FAQ ({form.faq.length})</span>}
            {videoUrl && <span style={{ fontSize: 10, padding: '3px 8px', background: 'var(--green-light)', color: 'var(--green-mid)', borderRadius: 20 }}>🎬 Vidéo</span>}
          </div>

        </div>
      </div>

      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: 'var(--ink-4)' }}>
        Mise à jour en temps réel
      </div>
    </div>
  );
}
// ─── FIN COMPOSANT PREVIEW ────────────────────────────────────────────


// ═══════════════════════════════════════════════════════════════════════
// DANS LE RETURN de NewListingPage, remplace :
//
//   {!isMobile && !isEdit && (
//     <div style={{ position: 'sticky', top: 80 }}>
//       ...tout le bloc preview actuel...
//     </div>
//   )}
//
// PAR :
//
//   {!isMobile && !isEdit && (
//     <ListingPreview
//       form={form}
//       photoPreviews={photoPreviews}
//       customAmenities={customAmenities}
//       transparencyScore={transparencyScore}
//       videoUrl={videoUrl}
//       step={step}
//       stepOffset={stepOffset}
//     />
//   )}
// ═══════════════════════════════════════════════════════════════════════

export default function NewListingPage({ onNavigate, listing }) {
  const isEdit = !!(listing?.id) && listing?.status !== 'draft';
  const isDraft = !!(listing?.id) && listing?.status === 'draft';
  console.log('listing status:', listing?.status, 'isEdit:', isEdit, 'isDraft:', isDraft); // 
  const [step, setStep]                       = useState(0);
  const [seasonOpen, setSeasonOpen]           = useState(false);
  const [seasonalPricing, setSeasonalPricing] = useState(false);
  const [customPrices, setCustomPrices]       = useState([{ id: 1, label: '', start: '', end: '', price: '', minNights: 1 }]);
  const [loading, setLoading]                 = useState(false);
  const [cguAccepted, setCguAccepted] = useState(false);
  const [charteAccepted, setCharteAccepted] = useState(false);
  const [error, setError]                     = useState(null);
  const [success, setSuccess]                 = useState(false);
  const [user, setUser]                       = useState(null);
  const [isManager, setIsManager]             = useState(false);
  const [hasExistingListing, setHasExistingListing] = useState(false);
  const [isMobile]                            = useState(window.innerWidth < 768);

  const [ownerEmail, setOwnerEmail]           = useState('');
  const [ownerName, setOwnerName]             = useState('');
  const [managerFee, setManagerFee]           = useState(20);
  const [feeMode, setFeeMode]                 = useState('pct');
  const [fixedFees, setFixedFees]             = useState({ fee_checkin: 0, fee_cleaning: 0, fee_linge: 0, fee_other: 0 });
  const [ownerInvited, setOwnerInvited]       = useState(false);
  const [ownerInviting, setOwnerInviting]     = useState(false);
  const [ownerInviteError, setOwnerInviteError] = useState(null);
  const [skipOwner, setSkipOwner]             = useState(false);

  const ALL_STEPS = isManager ? MANAGER_STEPS : BASE_STEPS;
  const STEPS = hasExistingListing ? ALL_STEPS.filter(s => s !== 'Documents') : ALL_STEPS;
  const stepOffset = isManager ? 2 : 1;

  const photoRef0 = useRef(); const photoRef1 = useRef(); const photoRef2 = useRef(); const photoRef3 = useRef();
  const photoRef4 = useRef(); const photoRef5 = useRef(); const photoRef6 = useRef(); const photoRef7 = useRef();
  const photoRefs = [photoRef0, photoRef1, photoRef2, photoRef3, photoRef4, photoRef5, photoRef6, photoRef7];
  const [photoPreviews, setPhotoPreviews]     = useState(() =>
    isEdit && listing.photos?.length ? [...listing.photos, ...Array(Math.max(0, 8 - listing.photos.length)).fill(null)] : Array(8).fill(null)
  );
  const [photoUploading, setPhotoUploading]   = useState(Array(8).fill(false));
  const [photoError, setPhotoError]           = useState(null);

  const videoRef = useRef();
  const [videoPreview, setvideoPreview]       = useState(isEdit ? (listing?.video_url || null) : null);
  const [videoUploading, setvideoUploading]   = useState(false);
  const [videoError, setvideoError]           = useState(null);
  const [videoUrl, setvideoUrl]               = useState(isEdit ? (listing?.video_url || null) : null);

  const [customAmenities, setCustomAmenities] = useState([]);
  const [newAmenityInput, setNewAmenityInput] = useState('');

  const [rulesExtra, setRulesExtra]           = useState('');
  const [rulesGenerating, setRulesGenerating] = useState(false);
  const [rulesError, setRulesError]           = useState(null);
  const [guideGenerating, setGuideGenerating] = useState(false);
  const [guideError, setGuideError]           = useState(null);
  const [emmaModal, setEmmaModal]             = useState(false);
  const [emmaTone, setEmmaTone]               = useState('');
  const [emmaGenerating, setEmmaGenerating]   = useState(false);
  const [emmaResult, setEmmaResult]           = useState(null);
  const [emmaGenStep, setEmmaGenStep]         = useState('');
  const [emmaChosenTitle, setEmmaChosenTitle] = useState('');
  const [applied, setApplied] = useState({});
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved]   = useState(false);
  const [draftId, setDraftId]         = useState(null);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [showDraftTip, setShowDraftTip] = useState(false);

  const [form, setForm] = useState(() => isEdit ? {
    title: listing.title || '',
    type: listing.type || '',
    category: listing.category || '',
    proximity: listing.proximity || '',
    target_audience: listing.target_audience || '',
    address: listing.address || '',
    city: listing.city || '',
    latitude: listing.latitude || null,
    longitude: listing.longitude || null,
    adults: listing.adults_count || listing.guests_max || 2,
    children: listing.children_count || 0,
    beds: listing.bedrooms || 1,
    baths: listing.bathrooms || 1,
    description: listing.description || '',
    amenities: AMENITIES_LIST.filter(a => (listing.amenities || []).includes(a.label)).map(a => a.id),
    priceNight: listing.price_night || '',
    cleaningFee: listing.price_cleaning || '',
    caution: listing.price_deposit || 0,
    taxeséjour: listing.taxe_sejour || '',
    checkinTime: listing.checkin_time || '15:00',
    checkoutTime: listing.checkout_time || '11:00',
    minNights: listing.min_nights || 1,
    smokingAllowed: listing.smoking_allowed || false,
    petsAllowed: listing.pets_allowed || false,
    partiesAllowed: listing.parties_allowed || false,
    photos: isEdit && listing.photos?.length
      ? [...listing.photos.slice(0, 8), ...Array(Math.max(0, 8 - listing.photos.length)).fill(null)]
      : Array(8).fill(null),
    registrationNumber: listing.registration_number || '',
    rules: '',
    extraRules: (listing.rules || '').split('\n\n').filter(Boolean),
    localGuide: listing.local_guide || { restaurants: '', plages: '', activités: '', transports: '' },
    faq: listing.faq || null,
  } : {
    title: '', type: '', category: '', address: '', city: '', proximity: '', target_audience: '', extraRules: [],
    latitude: null, longitude: null,
    adults: 2, children: 0, beds: 1, baths: 1,
    description: '', amenities: [],
    priceNight: '', cleaningFee: '', caution: 0, taxeséjour: '',
    checkinTime: '15:00', checkoutTime: '11:00',
    minNights: 1, smokingAllowed: false, petsAllowed: false, partiesAllowed: false,
    photos: Array(8).fill(null),
    registrationNumber: '', rules: '',
    localGuide: { restaurants: '', plages: '', activités: '', transports: '' },
    faq: null,
  });

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        const { data: existingListings } = await supabase.from('listings').select('id').eq('host_id', u.id).limit(1);
        setHasExistingListing(!!(existingListings && existingListings.length > 0));
        const { data: profile } = await supabase.from('host_profiles').select('is_manager').eq('id', u.id).single();
        setIsManager(profile?.is_manager === true);

        if (isDraft && listing) {
          setDraftId(listing.id);
          const knownLabels = AMENITIES_LIST.map(a => a.label);
          const customOnes = (listing.amenities || []).filter(a => !knownLabels.includes(a));
          setCustomAmenities(customOnes);
          setPhotoPreviews(listing.photos?.length 
            ? [...listing.photos, ...Array(Math.max(0, 8 - listing.photos.length)).fill(null)] 
            : Array(8).fill(null));
          if (listing.video_url) setvideoUrl(listing.video_url);
          if (listing.video_url) setvideoPreview(listing.video_url);
          setForm({
            title: listing.title || '',
            type: listing.type || '',
            category: listing.category || '',
            proximity: listing.proximity || '',
            target_audience: listing.target_audience || '',
            address: listing.address || '',
            city: listing.city || '',
            latitude: listing.latitude || null,
            longitude: listing.longitude || null,
            adults: listing.adults_count || listing.guests_max || 2,
            children: listing.children_count || 0,
            beds: listing.bedrooms || 1,
            baths: listing.bathrooms || 1,
            description: listing.description || '',
            amenities: AMENITIES_LIST.filter(a => (listing.amenities || []).includes(a.label)).map(a => a.id),
            priceNight: listing.price_night || '',
            cleaningFee: listing.price_cleaning || '',
            caution: listing.price_deposit || 0,
            taxeséjour: listing.taxe_sejour || '',
            checkinTime: listing.checkin_time || '15:00',
            checkoutTime: listing.checkout_time || '11:00',
            minNights: listing.min_nights || 1,
            smokingAllowed: listing.smoking_allowed || false,
            petsAllowed: listing.pets_allowed || false,
            partiesAllowed: listing.parties_allowed || false,
            photos: listing.photos?.length ? [...listing.photos, ...Array(Math.max(0, 8 - listing.photos.length)).fill(null)] : Array(8).fill(null),
            registrationNumber: listing.registration_number || '',
            rules: '',
            extraRules: (listing.rules || '').split('\n\n').filter(Boolean),
            localGuide: listing.local_guide || { restaurants: '', plages: '', activités: '', transports: '' },
            faq: listing.faq || null,
          });
          return;
        }

        const { data: draft } = await supabase
          .from('listings')
          .select('*')
          .eq('host_id', u.id)
          .eq('status', 'draft')
          .order('updated_at', { ascending: false })
          .limit(1)
          .single();

        if (draft && !isEdit && !isDraft) {
          setDraftId(draft.id);
          setPhotoPreviews(draft.photos?.length 
            ? [...draft.photos, ...Array(Math.max(0, 8 - draft.photos.length)).fill(null)] 
            : Array(8).fill(null));
          if (draft.video_url) setvideoUrl(draft.video_url);
          if (draft.video_url) setvideoPreview(draft.video_url);
          setForm({
            title: draft.title || '',
            type: draft.type || '',
            category: draft.category || '',
            proximity: draft.proximity || '',
            target_audience: draft.target_audience || '',
            address: draft.address || '',
            city: draft.city || '',
            latitude: draft.latitude || null,
            longitude: draft.longitude || null,
            adults: draft.adults_count || draft.guests_max || 2,
            children: draft.children_count || 0,
            beds: draft.bedrooms || 1,
            baths: draft.bathrooms || 1,
            description: draft.description || '',
            amenities: AMENITIES_LIST.filter(a => (draft.amenities || []).includes(a.label)).map(a => a.id),
            priceNight: draft.price_night || '',
            cleaningFee: draft.price_cleaning || '',
            caution: draft.price_deposit || 0,
            taxeséjour: draft.taxe_sejour || '',
            checkinTime: draft.checkin_time || '15:00',
            checkoutTime: draft.checkout_time || '11:00',
            minNights: draft.min_nights || 1,
            smokingAllowed: draft.smoking_allowed || false,
            petsAllowed: draft.pets_allowed || false,
            partiesAllowed: draft.parties_allowed || false,
            photos: draft.photos?.length ? [...draft.photos, ...Array(Math.max(0, 8 - draft.photos.length)).fill(null)] : Array(8).fill(null),
            registrationNumber: draft.registration_number || '',
            rules: '',
            extraRules: (draft.rules || '').split('\n\n').filter(Boolean),
            localGuide: draft.local_guide || { restaurants: '', plages: '', activités: '', transports: '' },
            faq: draft.faq || null,
          });
        }
      }
    });
  }, []);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleAmenity = (id) => update('amenities', form.amenities.includes(id) ? form.amenities.filter(a => a !== id) : [...form.amenities, id]);

  const handleInviteOwner = async () => {
    if (!ownerEmail || !ownerEmail.includes('@')) { setOwnerInviteError('Entrez un email valide.'); return; }
    setOwnerInviting(true); setOwnerInviteError(null);
    try {
      const { data: existingOwner } = await supabase.from('property_owners').select('id').eq('email', ownerEmail).single();
      let ownerId = existingOwner?.id;
      if (!ownerId) {
        const { data: newOwner, error: ownerErr } = await supabase.from('property_owners').insert({ email: ownerEmail, full_name: ownerName || ownerEmail.split('@')[0] }).select('id').single();
        if (ownerErr) throw ownerErr;
        ownerId = newOwner.id;
      }
      if (listing?.id) await supabase.from('listings').update({ owner_id: ownerId }).eq('id', listing.id);
      await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'owner_invitation', to: ownerEmail, data: { ownerName: ownerName || 'propriétaire', managerFee, inviteLink: window.location.origin + '/?page=owner-onboarding&owner_id=' + ownerId } }) });
      setOwnerInvited(true);
    } catch (e) {
      setOwnerInviteError('Erreur lors de l\'invitation : ' + e.message);
    } finally { setOwnerInviting(false); }
  };

  const handlePhotoUpload = async (index, file) => {
    if (!file || !user) return;
    setPhotoError(null);
    const preview = URL.createObjectURL(file);
    setPhotoPreviews(prev => { const n = [...prev]; n[index] = preview; return n; });
    setPhotoUploading(prev => { const n = [...prev]; n[index] = true; return n; });
    try {
      const ext  = file.name.split('.').pop();
      const path = `${user.id}/${Date.now()}_${index}.${ext}`;
      const { error: upErr } = await supabase.storage.from('Listingphotos').upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('Listingphotos').getPublicUrl(path);
      const updated = [...form.photos]; updated[index] = data.publicUrl; update('photos', updated);
    } catch (e) {
      setPhotoError(`Erreur upload : ${e.message}`);
      setPhotoPreviews(prev => { const n = [...prev]; n[index] = null; return n; });
      const updated = [...form.photos]; updated[index] = null; update('photos', updated);
    } finally {
      setPhotoUploading(prev => { const n = [...prev]; n[index] = false; return n; });
    }
  };

  const removePhoto = (index) => {
    setPhotoPreviews(prev => { const n = [...prev]; n[index] = null; return n; });
    const updated = [...form.photos]; updated[index] = null; update('photos', updated);
  };

  const handlevideoUpload = async (file) => {
  if (!file || !user) return;
  setvideoError(null);
  if (file.size > 500 * 1024 * 1024) { setvideoError('La vidéo ne doit pas dépasser 500 Mo.'); return; }
  if (!file.type.startsWith('video/')) { setvideoError('Format invalide. Utilisez MP4, MOV...'); return; }
  const duration = await new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });
  if (duration > 180) { setvideoError('La vidéo ne doit pas dépasser 3 minutes.'); return; }
  setvideoPreview(URL.createObjectURL(file));
  setvideoUploading(true);
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'havenly_videos');
    formData.append('cloud_name', 'doufhr0k4');
    const res = await fetch('https://api.cloudinary.com/v1_1/doufhr0k4/video/upload', {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    if (data.secure_url) {
  console.log('Cloudinary URL:', data.secure_url);
  setvideoUrl(data.secure_url);
} else {
  console.log('Cloudinary error:', data);
  throw new Error(data.error?.message || 'Erreur upload');
}
  } catch (e) {
    setvideoError('Erreur upload vidéo : ' + e.message);
    setvideoPreview(null); setvideoUrl(null);
  } finally { setvideoUploading(false); }
};
const removeVideo = () => { setvideoPreview(null); setvideoUrl(null); setvideoError(null); };

  const docRefs = { cni_recto: useRef(), cni_verso: useRef(), selfie: useRef(), assurance: useRef(), kbis: useRef() };
  const [docFiles,    setDocFiles]    = useState({ cni_recto: null, cni_verso: null, selfie: null, assurance: null, kbis: null });
  const [docPreviews, setDocPreviews] = useState({ cni_recto: null, cni_verso: null, selfie: null, assurance: null, kbis: null });
  const [docsUploading, setDocsUploading] = useState(false);
  const [docsSubmitted, setDocsSubmitted] = useState(false);
  const [docErrors, setDocErrors] = useState({});
  const [legalModal, setLegalModal] = useState(null);

  const DOCS_LIST = [
    { key: 'cni_recto',  label: "pièce d'identite (recto)", sublabel: 'CNI ou passeport - face avant', required: true },
    { key: 'cni_verso',  label: "pièce d'identite (verso)", sublabel: 'CNI - face arriere / passeport page 2', required: true },
    { key: 'selfie',     label: "Selfie avec votre pièce",  sublabel: 'Visage visible + document lisible', required: true },
    ...(isManager
      ? [{ key: 'kbis', label: 'Kbis de la societe', sublabel: 'Extrait Kbis de moins de 3 mois', required: true }]
      : [{ key: 'assurance', label: 'Attestation assurance', sublabel: 'Assurance habitation - location saisonniere recommandee', required: false }]
    ),
  ];

  const handleDocFile = (key, file) => {
    if (!file) return;
    setDocFiles(prev => ({ ...prev, [key]: file }));
    setDocPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
    setDocErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleDocDrop = (key, e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) handleDocFile(key, file); };

  const uploadDocFile = async (key, file) => {
    if (!user) return null;
    const ext  = file.name.split('.').pop();
    const path = `${user.id}/${key}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('hostdocuments').upload(path, file, { upsert: true, contentType: file.type });
    if (error) throw error;
    return path;
  };

  const handleDocsSubmit = async () => {
    const newErrors = {};
    if (!cguAccepted)    newErrors.cgu    = 'Vous devez accepter les CGU';
    if (!charteAccepted) newErrors.charte = 'Vous devez accepter la charte';
    const hasAtLeastOne = DOCS_LIST.some(d => docFiles[d.key]);
    if (!hasAtLeastOne) newErrors.general = 'Ajoutez au moins un document';
    setDocErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (!user) { onNavigate('auth'); return; }
    setDocsUploading(true);
    try {
      const paths = {};
      for (const doc of DOCS_LIST) {
        if (docFiles[doc.key]) paths[doc.key] = await uploadDocFile(doc.key, docFiles[doc.key]);
      }
      await supabase.from('host_profiles').update({
        status: 'pending',
        docs: { cni: !!(paths.cni_recto && paths.cni_verso), selfie: !!paths.selfie, assurance: !!paths.assurance, rib: false, kbis: !!paths.kbis },
        doc_urls: { cni_recto: paths.cni_recto || null, cni_verso: paths.cni_verso || null, selfie: paths.selfie || null, assurance: paths.assurance || null, kbis: paths.kbis || null },
        cgu_accepted: true, charte_accepted: true, updated_at: new Date().toISOString(),
      }).eq('id', user.id);
      const { data: hostProfile } = await supabase.from('host_profiles').select('full_name, email').eq('id', user.id).single();
      const hostName  = hostProfile?.full_name || 'Hote';
      const hostEmail = hostProfile?.email || user.email;
      await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'docs_submitted', to: 'contact@havenly.fr', data: { hostName, hostEmail, hostId: user.id, docsSubmitted: Object.keys(paths).join(', '), missingDocs: DOCS_LIST.filter(d => d.required && !paths[d.key]).map(d => d.label).join(', ') || 'Aucun' } }) }).catch(() => {});
      await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'docs_received', to: hostEmail, data: { name: hostName.split(' ')[0] } }) }).catch(() => {});
      setDocsSubmitted(true);
    } catch (err) {
      setDocErrors({ submit: 'Erreur upload : ' + err.message });
    } finally { setDocsUploading(false); }
  };

  const addCustomAmenity = () => {
    if (!newAmenityInput.trim()) return;
    setCustomAmenities(prev => [...prev, newAmenityInput.trim()]);
    setNewAmenityInput('');
  };

  const getCautionMsg = (val) => {
    if (val === 0)  return { text: 'Protection séjour désactivée - les dommages eventuels ne pourront pas être récupérés.', color: '#A32D2D', bg: '#FCEBEB' };
    if (val <= 150) return { text: 'Excellent - les logements avec Protection séjour <= 150 reçoivent 2x plus de demandes.', color: 'var(--green-mid)', bg: 'var(--green-light)' };
    if (val <= 250) return { text: 'Bon equilibre entre protection et attractivite.', color: 'var(--terra-mid)', bg: 'var(--terra-light)' };
    return { text: 'Attention - une Protection séjour elevee peut reduire vos reservations de 30%.', color: '#A32D2D', bg: '#FCEBEB' };
  };

  const cautionMsg        = getCautionMsg(form.caution);
  const requiredPhotos    = PHOTO_SLOTS.filter(s => s.required).length;
  const filledRequired    = PHOTO_SLOTS.filter(s => s.required && form.photos[s.id - 1]).length;
  const transparencyScore = calcScore(form);
  const totalGuests       = form.adults + form.children;
  const allAmenities      = [...form.amenities, ...customAmenities];

  const generateRulesWithAI = async () => {
    setRulesGenerating(true); setRulesError(null);
    try {
      const amenityLabels = form.amenities.map(id => AMENITIES_LIST.find(a => a.id === id)?.label || id);
      const hasPool = amenityLabels.some(a => a.toLowerCase().includes('piscine'));
      const hasBbq  = amenityLabels.some(a => a.toLowerCase().includes('barbecue'));
      const prompt = `Tu es un expert en location saisonniere. Genere un reglement interieur professionnel et concis.
IMPORTANT :
- Reponds en texte brut UNIQUEMENT, sans Markdown, sans #, sans **, sans symboles speciaux
- Uniquement des Règles reelles et verifiables, jamais d'inventions (pas d'assurance, pas de clauses legales inventees)
- Maximum 8 Règles courtes et claires
- Une regle par ligne, commencer par une majuscule
- Utilise les apostrophes typographiques correctes (l', d', c', j', n', etc.)
- Pas de categories, pas de titres
Type: ${form.type || 'Non precise'}, Ville: ${form.city || 'Non precisee'}, Capacite: ${form.adults + form.children} personnes, Chambres: ${form.beds}, Check-in: ${form.checkinTime}, Check-out: ${form.checkoutTime}, Fumeur: ${form.smokingAllowed ? 'Oui' : 'Non'}, Animaux: ${form.petsAllowed ? 'Oui' : 'Non'}, fêtes: ${form.partiesAllowed ? 'Oui' : 'Non'}${rulesExtra ? ', Specifique: ' + rulesExtra : ''}.`;
      const response = await fetch('/api/emma', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ generate: true, prompt, max_tokens: 1000 }) });
      const data = await response.json();
      if (data.text) {
  const lines = data.text.split('\n').filter(l => l.trim());
  update('extraRules', lines);
}
      else setRulesError('Erreur lors de la génération. Reessayez.');
    } catch (e) { setRulesError('Erreur de connexion. Reessayez.'); }
    finally { setRulesGenerating(false); }
  };

  const generateGuideWithAI = async () => {
    if (!form.city) { setGuideError("Renseignez votre ville d'abord."); return; }
    setGuideGenerating(true); setGuideError(null);
    try {
      const prompt = 'Tu es un expert local de ' + form.city + '. Guide pratique pour voyageurs. 2-3 recommandations par catégorie. Reponds UNIQUEMENT avec du JSON brut valide sans markdown. Format: {"restaurants":"Nom1 - desc.","plages":"Lieu1 - desc.","activités":"Act1 - desc.","transports":"Trans1 - desc."}';
      const response = await fetch('/api/emma', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ generate: true, prompt, max_tokens: 800 }) });
      const data = await response.json();
      const text = data.text || '';
      try {
        const clean = text.replace(/```json|```/g, '').trim();
        const guide = JSON.parse(clean);
        update('localGuide', { restaurants: guide.restaurants || '', plages: guide.plages || '', activités: guide.activités || '', transports: guide.transports || '' });
      } catch { setGuideError('Erreur de parsing. Reessayez.'); }
    } catch (e) { setGuideError('Erreur de connexion. Reessayez.'); }
    finally { setGuideGenerating(false); }
  };

  const handlePublish = async () => {
    const { data: { session: freshSession } } = await supabase.auth.getSession();
    if (!freshSession?.user) { onNavigate('auth'); return; }
    const currentUser = freshSession.user;

    const missing = [];
    if (!form.title?.trim())          missing.push('Titre de l\'annonce');
    if (!form.type)                   missing.push('Type de logement');
    if (!form.city?.trim())           missing.push('Ville');
    if (!form.address?.trim())        missing.push('Adresse');
    if (!form.priceNight || Number(form.priceNight) <= 0) missing.push('Prix par nuit');
    if (form.countryCode === 'fr' && (!form.taxeséjour || Number(form.taxeséjour) <= 0)) missing.push('Taxe de séjour (obligatoire en France)');
    if (!form.description || form.description.length < 100) missing.push('Description (100 caractères minimum)');
    if (missing.length > 0) { setError(`Champs manquants :\n- ${missing.join('\n- ')}`); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }

    setLoading(true); setError(null);
    try {
      const amenityLabels = [...form.amenities.map(id => AMENITIES_LIST.find(a => a.id === id)?.label || id), ...customAmenities];
      const payload = {
        title: form.title, description: form.description,
        city: form.city, address: form.address, type: form.type,
        category: form.category || null,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
        proximity: form.proximity || null,
        target_audience: form.target_audience || null,
        price_night: Number(form.priceNight), price_cleaning: Number(form.cleaningFee) || 0,
        price_deposit: form.caution, taxe_sejour: Number(form.taxeséjour), guests_max: totalGuests, adults_count: form.adults, children_count: form.children,
        min_nights: form.minNights || 1, bedrooms: form.beds, bathrooms: form.baths,
        amenities: amenityLabels, photos: form.photos.filter(Boolean),
        rules: [form.rules, ...(form.extraRules || [])].filter(Boolean).join('\n\n'),
        checkin_time: form.checkinTime, checkout_time: form.checkoutTime,
        custom_prices: customPrices.filter(cp => cp.price),
        registration_number: form.registrationNumber,
        region: form.region || null,
        country: form.country || null,
        country_code: form.countryCode || null,
        score_transparency: transparencyScore,
        video_url: videoUrl || null,
        local_guide: form.localGuide || null,
        faq: form.faq || null,
        manager_fee: isManager ? managerFee : null,
        fee_mode: isManager ? feeMode : null,
        fixed_fees: isManager && feeMode === 'fixed' ? fixedFees : null,
        updated_at: new Date().toISOString(),
      };

      let err;
      if (draftId) {
        ({ error: err } = await supabase.from('listings').update({ ...payload, status: 'pending' }).eq('id', draftId));
      } else if (isEdit) {
        ({ error: err } = await supabase.from('listings').update(payload).eq('id', listing.id));
      } else {
        ({ error: err } = await supabase.from('listings').insert({ ...payload, host_id: currentUser.id, status: 'pending' }));
        await supabase.from('host_profiles').update({ level: 'havenly', updated_at: new Date().toISOString() }).eq('id', currentUser.id);
      }
      if (err) throw err;
      await fetch('/api/send-email', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    type: 'new_listing',
    to: 'contact@havenly.fr',
    data: {
      hostName: currentUser.email?.split('@')[0] || 'Hôte',
      hostEmail: currentUser.email,
      listingTitle: form.title,
      listingCity: form.city,
      priceNight: form.priceNight,
    }
  })
}).catch(() => {});
      setSuccess(true);
    } catch (e) {
      setError('Une erreur est survenue. Veuillez reessayer.');
    } finally { setLoading(false); }
  };
  
  const handleSaveDraft = async () => {
  console.log('handleSaveDraft called');
  console.log('videoUrl au moment du brouillon:', videoUrl);
  console.log('videoUrl avant save:', videoUrl);
  const { data: { session } } = await supabase.auth.getSession();
  console.log('session:', session?.user?.id);
  if (!session?.user) { onNavigate('auth'); return; }
  setDraftSaving(true);
  console.log('payload avant insert:', {
  type: form.type,
  city: form.city,
  price_night: Number(form.priceNight) || 0,
  taxe_sejour: Number(form.taxeSejour) || 0,
  draftId
});
  try {
    console.log('form type:', form.type, 'city:', form.city);
      const amenityLabels = [...form.amenities.map(id => AMENITIES_LIST.find(a => a.id === id)?.label || id), ...customAmenities];
      const payload = {
        title: form.title || 'Brouillon sans titre',
        description: form.description,
        city: form.city, address: form.address, type: form.type,
        category: form.category || null,
        latitude: form.latitude || null, longitude: form.longitude || null,
        proximity: form.proximity || null, target_audience: form.target_audience || null,
        price_night: Number(form.priceNight) || 0,
        price_cleaning: Number(form.cleaningFee) || 0,
        price_deposit: form.caution,
        taxe_sejour: Number(form.taxeséjour) || 0,
        guests_max: form.adults + form.children || 1,
        adults_count: form.adults,
        children_count: form.children,
        min_nights: form.minNights || 1,
        bedrooms: form.beds, bathrooms: form.baths,
        amenities: amenityLabels,
        photos: form.photos.filter(Boolean),
        rules: [form.rules, ...(form.extraRules || [])].filter(Boolean).join('\n\n'),
        checkin_time: form.checkinTime, checkout_time: form.checkoutTime,
        registration_number: form.registrationNumber,
        region: form.region || null,
        country: form.country || null,
        country_code: form.countryCode || null,
        video_url: videoUrl || null,
        local_guide: form.localGuide || null,
        faq: form.faq || null,
        status: 'draft',
        updated_at: new Date().toISOString(),
      };
      if (draftId) {
  const { error: updateErr } = await supabase.from('listings').update(payload).eq('id', draftId);
  if (updateErr) console.error('update error:', updateErr);
} else {
        const { data, error } = await supabase.from('listings')
          .insert({ ...payload, host_id: session.user.id })
          .select('id').single();
        if (error) throw error;
        setDraftId(data.id);
      }

      await fetch('/api/send-email', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    type: 'new_listing',
    to: 'contact@havenly.fr',
    data: {
      hostName: session.user.email?.split('@')[0] || 'Hôte',
      hostEmail: session.user.email,
      listingTitle: form.title || 'Brouillon sans titre',
      listingCity: form.city || '—',
      priceNight: form.priceNight || '—',
      isDraft: true,
    }
  })
}).catch(() => {});
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch(e) {
      console.error('handleSaveDraft error:', e);
    } finally { setDraftSaving(false); }
  };

  
  const inputStyle  = { width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', color: 'var(--ink)', background: 'var(--white)', transition: 'border-color 0.15s', boxSizing: 'border-box' };
  const labelStyle  = { fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 };
  const sectionCard = { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16 };

  if (success) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', paddingTop: 80 }}>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 20, padding: '2.5rem', maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--green-mid)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 26, letterSpacing: '-0.5px', marginBottom: 12 }}>
          {isEdit ? 'Annonce mise a jour !' : 'Annonce soumise !'}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 20 }}>
          {isEdit ? 'Vos modifications ont ete enregistrees.' : <>Score de transparence : <strong style={{ color: 'var(--green-mid)' }}>{transparencyScore}/100</strong>. Verification sous 24h.</>}
        </p>
        <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 12, padding: '14px 16px', marginBottom: 24, textAlign: 'left' }}>
          {(isEdit
            ? ['Modifications enregistrees', 'Prix et disponibilites mis a jour en temps reel']
            : ['Verification manuelle sous 24h', 'Email de confirmation a la publication', 'Emma activee automatiquement', 'Pensez a déclarer votre activite a votre assureur']
          ).map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--green-mid)', marginBottom: 6 }}><span>+</span><span>{item}</span></div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => onNavigate('dashboard')} style={{ padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Mon dashboard</button>
          <button onClick={() => onNavigate('hostdocs')} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Documents requis</button>
        </div>
      </div>
    </div>
  );

   const MOTIVATIONS = [
  "C'est parti ! Votre logement mérite d'être découvert. 🏡",
  "Super, les photos font toute la différence — vous y êtes presque !",
  "Excellent ! Les équipements rassurent les voyageurs. Encore un effort !",
  "Vous avancez très bien ! Plus que quelques détails.",
  "Presque là ! Les règles protègent vous et vos voyageurs.",
  "Incroyable — votre guide local va faire la différence. 🌟",
  "Dernière ligne droite ! Vous rejoignez les meilleurs hôtes Havenly. 🎉",
  "C'est fait ! Votre annonce est prête à accueillir vos voyageurs.",
];
  
  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: isMobile ? '0 1rem' : '0 2rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 0' }}>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: 20, letterSpacing: '-0.5px' }}>{isEdit ? 'Modifier mon annonce' : 'Publier une annonce'}</h1>
              {step > 0 && (
  <div style={{ fontSize: 13, color: 'var(--green-mid)', fontStyle: 'italic', marginTop: 4 }}>
    {MOTIVATIONS[step - 1]}
  </div>
)}
            </div>
            <button onClick={() => onNavigate('dashboard')} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer' }}>Retour</button>
          </div>
          <div style={{ marginTop: 12, marginBottom: 4 }}>
  <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
    <div style={{ width: `${((step + 1) / STEPS.length) * 100}%`, height: '100%', background: 'var(--green)', borderRadius: 2, transition: 'width 0.4s ease' }} />
  </div>
</div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '1rem' : '2rem', display: isMobile ? 'block' : 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'start' }}>   <div>
        {error && (
          <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>
            {error.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}

        {/* ═══ ETAPE 0 — propriétaire (conciergerie) ═══ */}
        {step === 0 && isManager && (
          <div style={sectionCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#FDF0E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏠</div>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Informations propriétaire</h2>
                <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '4px 0 0' }}>Invitez le propriétaire du bien pour un reversement transparent et automatique.</p>
              </div>
            </div>
            <div style={{ background: '#FDF8F4', border: '1px solid #E8A87C', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#C4784A', marginBottom: 6 }}>Reversement transparent au propriétaire</div>
              <p style={{ fontSize: 12, color: '#8B5A3A', lineHeight: 1.65, margin: 0 }}>Le propriétaire recoit sa part directement via Stripe a chaque reservation. Vos honoraires sont verses separement, automatiquement.</p>
            </div>
            {ownerInvited ? (
              <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 12, padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--green-mid)', marginBottom: 4 }}>Invitation envoyee !</div>
                <button onClick={() => setStep(step + 1)} style={{ marginTop: 16, padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Continuer</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Prenom et nom du propriétaire</label>
                    <input style={inputStyle} placeholder="Jean Dupont" value={ownerName} onChange={e => setOwnerName(e.target.value)} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email du propriétaire *</label>
                    <input style={inputStyle} type="email" placeholder="jean@exemple.fr" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Mode de facturation</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {[{ key: 'pct', label: 'Pourcentage', desc: '% des revenus' }, { key: 'fixed', label: 'Forfait fixe', desc: 'Montant par reservation' }].map(m => (
                      <button key={m.key} onClick={() => setFeeMode(m.key)} style={{ padding: '10px 12px', border: `2px solid ${feeMode === m.key ? '#C4784A' : 'var(--border)'}`, borderRadius: 10, background: feeMode === m.key ? '#FDF8F4' : 'var(--white)', cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: feeMode === m.key ? '#C4784A' : 'var(--ink)' }}>{m.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{m.desc}</div>
                      </button>
                    ))}
                  </div>
                  {feeMode === 'pct' ? (
                    <div>
                      <label style={labelStyle}>Vos honoraires ({managerFee}%)</label>
                      <input type="range" min={5} max={40} step={1} value={managerFee} onChange={e => setManagerFee(Number(e.target.value))} style={{ width: '100%', accentColor: '#C4784A', marginBottom: 8 }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-4)' }}>
                        <span>5% (minimal)</span><span style={{ color: '#C4784A', fontWeight: 500 }}>{managerFee}% selectionne</span><span>40% (premium)</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label style={labelStyle}>Forfait par reservation (€)</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[{ key: 'fee_checkin', label: 'Accueil voyageur', placeholder: 'Ex: 30' }, { key: 'fee_cleaning', label: 'ménage', placeholder: 'Ex: 60' }, { key: 'fee_linge', label: 'Linge de maison', placeholder: 'Ex: 20' }, { key: 'fee_other', label: 'Autre prestation', placeholder: 'Ex: 15' }].map(f => (
                          <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 8, alignItems: 'center' }}>
                            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{f.label}</div>
                            <input type="number" min={0} placeholder={f.placeholder} value={fixedFees[f.key] || ''} onChange={e => setFixedFees(prev => ({ ...prev, [f.key]: Number(e.target.value) }))} style={{ ...inputStyle, fontSize: 13 }} onFocus={e => e.target.style.borderColor = '#C4784A'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={handleInviteOwner} disabled={ownerInviting || !ownerEmail}
                    style={{ padding: '11px 24px', background: ownerInviting || !ownerEmail ? 'var(--border)' : '#C4784A', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: ownerInviting || !ownerEmail ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)' }}>
                    {ownerInviting ? 'Envoi...' : 'Inviter le propriétaire'}
                  </button>
                  <button onClick={() => { setSkipOwner(true); setStep(step + 1); }}
                    style={{ padding: '11px 24px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                    Passer pour l'instant
                  </button>
                </div>
                {ownerInviteError && <div style={{ fontSize: 13, color: '#A32D2D' }}>{ownerInviteError}</div>}
              </div>
            )}
          </div>
        )}

        {/* ═══ ETAPE Emma IA ═══ */}
{step === (isManager ? 1 : 0) && !isEdit && (
  <div style={sectionCard}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>✨</div>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0, fontFamily: 'var(--serif)' }}>Bonjour, je suis Emma !</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '4px 0 0', lineHeight: 1.6 }}>Votre assistante IA Havenly. Je vais créer votre annonce avec vous.</p>
      </div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
      {[
        { icon: '📸', title: 'Uploadez vos photos', desc: 'À l\'étape suivante — c\'est rapide et c\'est tout ce dont j\'ai besoin pour commencer.' },
        { icon: '📝', title: 'Renseignez quelques infos clés', desc: 'Type de logement, ville, capacité. Emma s\'occupe du reste.' },
        { icon: '🚀', title: 'Emma génère votre annonce', desc: 'Titre accrocheur, description complète, FAQ voyageurs, traductions. En quelques secondes.' },
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 16px', background: 'var(--surface)', borderRadius: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 3 }}>{item.title}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{item.desc}</div>
          </div>
        </div>
      ))}
    </div>

    <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, color: '#534AB7', lineHeight: 1.7 }}>
        💡 <strong>Le saviez-vous ?</strong> Les annonces générées avec Emma reçoivent en moyenne <strong>+40% de réservations</strong> grâce à des descriptions optimisées et des traductions EN/ES/DE incluses.
      </div>
    </div>

    <div style={{ display: 'flex', gap: 10 }}>
      <button onClick={() => setStep(step + 1)}
        style={{ padding: '12px 28px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
        ✨ Créer mon annonce avec Emma
      </button>
      <button onClick={() => setStep(step + 1)}
        style={{ padding: '12px 20px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
        Passer
      </button>
    </div>
  </div>
)}

        {/* ═══ ETAPE Logement ═══ */}   
        
        
        {/* ═══ ETAPE Logement ═══ */}
        {step === 1 + stepOffset && (
          <div style={sectionCard}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Informations générales</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Titre de l'annonce *</label>
                <input style={inputStyle} placeholder="Ex: Villa avec piscine vue mer" value={form.title}
                  onChange={e => update('title', e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--green)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                {form.type && form.city && (
                  <div style={{ fontSize: 11, color: 'var(--green-mid)', marginTop: 6, fontStyle: 'italic', cursor: 'pointer' }}
                    onClick={() => update('title', getTitleSuggestion(form.type, form.city))}>
                    Suggestion : "{getTitleSuggestion(form.type, form.city)}" — cliquez pour utiliser
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Type de logement *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                  {['Appartement', 'Maison', 'Villa', 'Chalet', 'Studio', 'Chambre', 'Loft', 'Mas'].map(type => (
                    <button key={type} onClick={() => update('type', type)}
                      style={{ padding: '10px', border: `1px solid ${form.type === type ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, background: form.type === type ? 'var(--green-light)' : 'var(--white)', color: form.type === type ? 'var(--green-mid)' : 'var(--ink-3)', fontSize: 13, fontWeight: form.type === type ? 500 : 400, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── CATÉGORIE (nouveau) ── */}
              <div>
                <label style={labelStyle}>catégorie / Environnement</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                  {[
                    { id: 'mer',      label: 'Mer',      icon: '' },
                    { id: 'montagne', label: 'Montagne', icon: '' },
                    { id: 'campagne', label: 'Campagne', icon: '' },
                    { id: 'ville',    label: 'Ville',    icon: '' },
                    { id: 'lac',      label: 'Lac',      icon: '' },
                    { id: 'luxe',     label: 'Luxe',     icon: '' },
                  ].map(cat => (
                    <button key={cat.id} onClick={() => update('category', form.category === cat.id ? '' : cat.id)}
                      style={{ padding: '10px', border: `1px solid ${form.category === cat.id ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, background: form.category === cat.id ? 'var(--green-light)' : 'var(--white)', color: form.category === cat.id ? 'var(--green-mid)' : 'var(--ink-3)', fontSize: 13, fontWeight: form.category === cat.id ? 500 : 400, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                      {cat.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>Aide les voyageurs à filtrer par environnement sur la Page de recherche</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Adresse *</label>
                  <input style={inputStyle} placeholder="12 rue des Oliviers" value={form.address}
                    onChange={e => update('address', e.target.value)}
                    onFocus={e => e.target.style.borderColor = 'var(--green)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                </div>
                <div>
                  <label style={labelStyle}>Ville *</label>
                  <CityInput
  value={form.city}
  onChange={(val, coords) => {
    update('city', val);
    if (coords) {
      update('latitude', coords.lat);
      update('longitude', coords.lng);
      if (coords.countryCode) update('countryCode', coords.countryCode);
      if (coords.region) update('region', coords.region);
      if (coords.country) update('country', coords.country);
    }
  }}
  inputStyle={inputStyle}
/>
                  {form.latitude && (
                    <div style={{ fontSize: 11, color: 'var(--green-mid)', marginTop: 4 }}>
                      GPS enregistre : {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={labelStyle}>capacité</label>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
                  {[
                    { key: 'adults',   label: 'Adultes',       min: 1, max: 16 },
                    { key: 'children', label: 'Enfants',        min: 0, max: 10 },
                    { key: 'beds',     label: 'Chambres',       min: 1, max: 10 },
                    { key: 'baths',    label: 'Salles de bain', min: 1, max: 6  },
                  ].map(field => (
                    <div key={field.key}>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>{field.label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px' }}>
                        <button onClick={() => update(field.key, Math.max(field.min, form[field.key] - 1))} style={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid var(--border-2)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</button>
                        <span style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 500 }}>{form[field.key]}</span>
                        <button onClick={() => update(field.key, Math.min(field.max, form[field.key] + 1))} style={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid var(--border-2)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
                  capacité totale : <strong>{totalGuests} voyageur{totalGuests > 1 ? 's' : ''}</strong>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
  <div>
    <label style={labelStyle}>Heure de check-in</label>
    <select style={inputStyle} value={form.checkinTime} onChange={e => update('checkinTime', e.target.value)}>
      {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(t => <option key={t}>{t}</option>)}
    </select>
  </div>
  <div>
    <label style={labelStyle}>Heure de check-out</label>
    <select style={inputStyle} value={form.checkoutTime} onChange={e => update('checkoutTime', e.target.value)}>
      {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(t => <option key={t}>{t}</option>)}
    </select>
  </div>
</div>
              </div>

              

              
              {/* ── Proximité ── */}
<div>
  <label style={labelStyle}>Proximité du logement <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 400 }}>(optionnel)</span></label>
  <input style={inputStyle} placeholder="Ex: 200m de la plage, 5min centre-ville a pied, gare a 10min..."
    value={form.proximity}
    onChange={e => update('proximity', e.target.value)}
    onFocus={e => e.target.style.borderColor = 'var(--green)'}
    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
</div>

{/* ── CIBLE PRINCIPALE ── */}
<div>
  <label style={labelStyle}>Cible principale <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 400 }}>(optionnel)</span></label>
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {['Familles avec enfants', 'Couples', 'Télétravail', 'Groupes d\'amis', 'Voyageurs d\'affaires', 'Accessibilité PMR', 'Animaux bienvenus', 'Bien-être'].map(t => (
      <button key={t} onClick={() => {
  const current = form.target_audience ? form.target_audience.split(',') : [];
  const updated = current.includes(t) ? current.filter(x => x !== t) : [...current, t];
  update('target_audience', updated.join(','));
}}
        style={{ padding: '7px 14px', border: `1px solid ${(form.target_audience || '').split(',').map(s => s.trim()).includes(t) ? 'var(--green)' : 'var(--border)'}`, borderRadius: 20, background: (form.target_audience || '').split(',').map(s => s.trim()).includes(t) ? 'var(--green-light)' : 'var(--white)', color: (form.target_audience || '').split(',').map(s => s.trim()).includes(t) ? 'var(--green-mid)' : 'var(--ink-3)', fontSize: 13, fontWeight: (form.target_audience || '').split(',').map(s => s.trim()).includes(t) ? 500 : 400, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
        {t}
      </button>
    ))}
  </div> 
</div>
<div>
                <label style={labelStyle}>Description *</label>
                <textarea style={{ ...inputStyle, height: 120, resize: 'vertical', lineHeight: 1.6 }}
                  placeholder="Décrivez votre logement — situation, ambiance, points forts..."
                  value={form.description} onChange={e => update('description', e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--green)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <div style={{ fontSize: 11, color: form.description.length < 100 ? 'var(--terra-mid)' : 'var(--green-mid)', marginTop: 4 }}>
                  {form.description.length} / 100 caractères minimum {form.description.length >= 100 && '· +20 pts'}
                </div>
              </div>

              {/* ── EMMA — Generateur de description ── */}
              <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#534AB7', marginBottom: 4 }}>
                  Générer mon annonce avec Emma IA
                </div>
                <div style={{ fontSize: 12, color: '#534AB7', marginBottom: 10, lineHeight: 1.6 }}>
                  Emma va rédiger le titre, la description, les points forts et la FAQ.
                  {(!form.type || !form.city) && <span style={{ color: '#A32D2D' }}> Renseignez d'abord le type et la ville.</span>}
                </div>
                <button onClick={() => { setEmmaModal(true); setEmmaResult(null); setEmmaTone(''); setEmmaGenerating(false); }} disabled={!form.type || !form.city}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: (!form.type || !form.city) ? '#AFA9EC' : '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: (!form.type || !form.city) ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  {(!form.type || !form.city) ? 'Renseignez type et ville d\'abord' : 'Générer avec Emma IA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ ETAPE Photos ═══ */}
        {step === (isManager ? 2 : 1) && (
          <div style={sectionCard}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Photos de votre logement</h2>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8, lineHeight: 1.6 }}>
              Les 5 premières photos sont <strong style={{ fontWeight: 500, color: 'var(--ink)' }}>obligatoires</strong>. Les annonces avec 5 photos minimum reçoivent en moyenne plus de réservations.
            </p>
            <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#A32D2D', lineHeight: 1.6 }}>
              <strong style={{ fontWeight: 500 }}>Important :</strong> ces photos serviront d'état des lieux initial.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {PHOTO_SLOTS.map((slot, i) => {
                const preview = photoPreviews[i];
                const uploaded = form.photos[i];
                const isUploading = photoUploading[i];
                return (
                  <div key={slot.id} style={{ border: `2px dashed ${uploaded ? 'var(--green)' : slot.required ? 'var(--border-2)' : 'var(--border)'}`, borderRadius: 12, overflow: 'hidden', background: uploaded ? 'var(--green-light)' : 'var(--surface)' }}>
                    <div style={{ height: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', padding: 12 }}
                      onClick={() => !isUploading && photoRefs[i].current.click()}>
                      {isUploading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, border: '3px solid var(--green-light)', borderTop: '3px solid var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          <span style={{ fontSize: 11, color: 'var(--green-mid)' }}>Upload en cours...</span>
                        </div>
                      ) : preview ? (
                        <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', borderRadius: 6 }}>
                          <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ) : (
                        <>
                          <div style={{ width: 44, height: 44, borderRadius: '50%', background: slot.required ? 'var(--terra-light)' : 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: slot.required ? 'var(--terra-mid)' : 'var(--ink-3)' }}>
                            {slot.required ? <CameraIcon /> : <PlusIcon />}
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--ink-4)', textAlign: 'center' }}>Cliquez pour ajouter</span>
                        </>
                      )}
                    </div>
                    <input ref={photoRefs[i]} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                      onChange={e => handlePhotoUpload(i, e.target.files[0])} />
                    <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', background: 'var(--white)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ color: 'var(--ink-3)', flexShrink: 0 }}><slot.Icon /></span>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)' }}>{slot.label}{slot.required && <span style={{ color: '#E24B4A', marginLeft: 3 }}>*</span>}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.4 }}>{slot.tip}</div>
                      {uploaded && !isUploading && (
                        <button onClick={e => { e.stopPropagation(); removePhoto(i); }}
                          style={{ marginTop: 4, fontSize: 11, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--sans)' }}>
                          x Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {photoError && <div style={{ marginTop: 12, background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#A32D2D' }}>{photoError}</div>}
            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
              {filledRequired}/{requiredPhotos} photos obligatoires ajoutées
            </div>
            {/* Vidéo */}
            <div style={{ marginTop: 24, background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--green-mid)" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Vidéo de presentation</div>
                <span style={{ background: 'var(--green-light)', color: 'var(--green-mid)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20 }}>Exclusif Havenly</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.6 }}>Ajoutez une Vidéo de visite (60s max, 100Mo max). Les annonces avec Vidéo reçoivent +60% de vues.</p>
              {videoPreview ? (
                <div>
                  <video src={videoPreview} controls style={{ width: '100%', maxHeight: 220, display: 'block', borderRadius: 10, marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => videoRef.current.click()} style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', color: 'var(--ink-3)' }}>Changer la Vidéo</button>
                    <button onClick={removeVideo} style={{ padding: '8px 16px', background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', color: '#A32D2D' }}>Supprimer</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => videoRef.current.click()} style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '32px', textAlign: 'center', cursor: 'pointer', background: 'var(--white)' }}>
                  {videoUploading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, border: '3px solid var(--green-light)', borderTop: '3px solid var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      <span style={{ fontSize: 13, color: 'var(--green-mid)' }}>Upload en cours...</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Cliquez pour ajouter une Vidéo</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>MP4, MOV, AVI · 60s max · 100 Mo max</div>
                    </>
                  )}
                </div>
              )}
              <input ref={videoRef} type="file" accept="Vidéo/*" style={{ display: 'none' }} onChange={e => handlevideoUpload(e.target.files[0])} />
              {videoError && <div style={{ marginTop: 10, background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#A32D2D' }}>{videoError}</div>}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* ═══ ETAPE Équipements ═══ */}
        {step === 2 + stepOffset && (
          <div style={sectionCard}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>Équipements disponibles</h2>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>Ajoutez au moins 5 équipements pour augmenter votre score de transparence.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
              {AMENITIES_LIST.map(a => (
                <button key={a.id} onClick={() => toggleAmenity(a.id)}
                  style={{ padding: '12px', border: `1px solid ${form.amenities.includes(a.id) ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, background: form.amenities.includes(a.id) ? 'var(--green-light)' : 'var(--white)', color: form.amenities.includes(a.id) ? 'var(--green-mid)' : 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: form.amenities.includes(a.id) ? 500 : 400 }}>
                  <span style={{ display: 'flex', flexShrink: 0 }}><a.Icon /></span>
                  {a.label}
                </button>
              ))}
              {customAmenities.map((a, i) => (
                <div key={`custom-${i}`} style={{ padding: '12px', border: '1px solid var(--green)', borderRadius: 10, background: 'var(--green-light)', color: 'var(--green-mid)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 500 }}>
                  <span>{a}</span>
                  <button onClick={() => setCustomAmenities(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-mid)', fontSize: 16, lineHeight: 1, padding: 0 }}>x</button>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '14px 16px', border: '1px dashed var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--ink-3)' }}>Autre équipement non listé ?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newAmenityInput} onChange={e => setNewAmenityInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustomAmenity()}
                  placeholder="Ex: Sauna, Tennis, Salle de jeux..."
                  style={{ ...inputStyle, flex: 1, fontSize: 13 }} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <button onClick={addCustomAmenity} style={{ padding: '10px 16px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', flexShrink: 0 }}>Ajouter</button>
              </div>
            </div>
            <div style={{ marginTop: 14, fontSize: 13, color: allAmenities.length >= 5 ? 'var(--green-mid)' : 'var(--ink-3)', textAlign: 'center' }}>
              {allAmenities.length} équipement{allAmenities.length !== 1 ? 's' : ''} selectionne{allAmenities.length !== 1 ? 's' : ''}{allAmenities.length >= 5 && ' · +15 pts'}
            </div>
          </div>
        )}

        {/* ═══ ETAPE Tarifs ═══ */}
        {step === 3 + stepOffset && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={sectionCard}>
              <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Vos tarifs</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Prix par nuit (€) *</label>
                    <input style={inputStyle} type="number" placeholder="Ex: 120" value={form.priceNight}
                      onChange={e => update('priceNight', e.target.value)}
                      onFocus={e => e.target.style.borderColor = 'var(--green)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                    {form.type && <div style={{ fontSize: 11, color: 'var(--green-mid)', marginTop: 4 }}>Fourchette {form.type} : {getPriceRange(form.type)}</div>}
                
                  </div>
                  <div>
                    <label style={labelStyle}>Frais de ménage (€)</label>
                    <input style={inputStyle} type="number" placeholder="Ex: 50" value={form.cleaningFee}
                      onChange={e => update('cleaningFee', e.target.value)}
                      onFocus={e => e.target.style.borderColor = 'var(--green)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  </div>
                </div>
                {form.priceNight && (
                  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Simulation pour 3 nuits</div>
                    {[
                      { label: `${form.priceNight} x 3 nuits`, val: `${form.priceNight * 3} €` },
                      { label: 'Frais de ménage', val: `${form.cleaningFee || 0} €` },
                      { label: 'Commission Havenly (8%)', val: `-${Math.round((form.priceNight * 3 + Number(form.cleaningFee || 0)) * 0.08)} €` },
                      { label: 'Vous recevez', val: `${Math.round((form.priceNight * 3 + Number(form.cleaningFee || 0)) * 0.92)} €`, bold: true, green: true },
                    ].map(row => (
                      <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: row.bold ? '10px 0 0' : '5px 0', borderTop: row.bold ? '1px solid var(--border)' : 'none', marginTop: row.bold ? 6 : 0 }}>
                        <span style={{ color: 'var(--ink-3)' }}>{row.label}</span>
                        <span style={{ fontWeight: row.bold ? 500 : 400, color: row.green ? 'var(--green-mid)' : 'var(--ink)' }}>{row.val}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 12, padding: '14px 16px' }}>
  <div style={{ fontSize: 13, fontWeight: 500, color: '#534AB7', marginBottom: 6 }}>
    Loi Le Meur 2024 — Déclaration obligatoire
  </div>
  <p style={{ fontSize: 12, color: '#534AB7', marginBottom: 10, lineHeight: 1.6 }}>
    Tout meublé de tourisme doit être déclaré en mairie. +20 pts au score de transparence.
  </p>
  <label style={{ fontSize: 13, fontWeight: 500, color: '#534AB7', display: 'block', marginBottom: 6 }}>
    Numéro d'enregistrement mairie *
  </label>
  <input style={inputStyle} placeholder="Ex: 06088-24-00123" value={form.registrationNumber}
    onChange={e => update('registrationNumber', e.target.value)}
    onFocus={e => e.target.style.borderColor = '#534AB7'}
    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
</div>
            <div style={{ background: '#FDF8F4', border: '1px solid #E8A87C', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#C4784A', marginBottom: 6 }}>Taxe de séjour - à votre charge *</div>
              <p style={{ fontSize: 12, color: '#8B5A3A', lineHeight: 1.65, marginBottom: 12 }}>Havenly ne collecte pas la taxe de séjour. Vous êtes responsable de la déclarer et de la reverser à votre mairie.</p>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#C4784A', display: 'block', marginBottom: 6 }}>Montant par nuit (€/pers) *</label>
              <input type="number" min="0" step="0.01" placeholder="Ex: 1.65" value={form.taxeséjour}
                onChange={e => update('taxeséjour', e.target.value)}
                style={{ ...inputStyle, width: 180 }}
                onFocus={e => e.target.style.borderColor = 'var(--green)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
            <div style={sectionCard}>
              <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>Protection séjour Havenly</h2>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16, lineHeight: 1.6 }}>Autorisation bancaire bloquée sur la carte du voyageur — liberée 48h après départ. +10 pts.</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>Montant de la Protection séjour</span>
                <span style={{ fontSize: 22, fontWeight: 500 }}>{form.caution === 0 ? 'Désactivée' : `${form.caution} €`}</span>
              </div>
              <input type="range" min={0} max={500} step={10} value={form.caution} onChange={e => update('caution', Number(e.target.value))} style={{ width: '100%', marginBottom: 10, accentColor: 'var(--green)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-4)', marginBottom: 12 }}>
                <span>0 (désactivée)</span><span style={{ color: 'var(--green)', fontWeight: 500 }}>Recommande : 70 €</span><span>500 €</span>
              </div>
              <div style={{ background: cautionMsg.bg, borderRadius: 10, padding: '10px 14px' }}>
                <p style={{ fontSize: 13, color: cautionMsg.color, lineHeight: 1.6, margin: 0 }}>{cautionMsg.text}</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══ ETAPE Règles ═══ */}
        {step === 4 + stepOffset && (
          <div style={sectionCard}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Règles du logement</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Durée minimale */}
              <div>
                <label style={labelStyle}>Durée minimale de séjour</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px', width: 'fit-content' }}>
                  <button onClick={() => update('minNights', Math.max(1, form.minNights - 1))} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border-2)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)' }}>-</button>
                  <span style={{ fontSize: 15, fontWeight: 500, minWidth: 80, textAlign: 'center' }}>{form.minNights} nuit{form.minNights > 1 ? 's' : ''}</span>
                  <button onClick={() => update('minNights', Math.min(30, form.minNights + 1))} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border-2)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)' }}>+</button>
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { key: 'smokingAllowed', label: 'Fumeur autorisé', desc: 'Les voyageurs peuvent fumer dans le logement' },
                  { key: 'petsAllowed', label: 'Animaux acceptés', desc: 'Les voyageurs peuvent venir avec leurs animaux' },
                  { key: 'partiesAllowed', label: 'Fêtes autorisées', desc: 'Les événements et rassemblements sont permis' },
                ].map(rule => (
                  <div key={rule.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'var(--surface)', borderRadius: 10 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{rule.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{rule.desc}</div>
                    </div>
                    <div onClick={() => update(rule.key, !form[rule.key])} style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', background: form[rule.key] ? 'var(--green)' : 'var(--border-2)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form[rule.key] ? 23 : 3, transition: 'left 0.2s' }} />
                    </div>
                  </div>
                ))}
              </div>
              {/* Règles supplémentaires */}
              <div>
                <label style={labelStyle}>Règles supplémentaires <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 400 }}>(optionnel)</span></label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(form.extraRules || []).map((rule, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input style={{ ...inputStyle, flex: 1 }} value={rule}
                        onChange={e => { const r = [...(form.extraRules || [])]; r[i] = e.target.value; update('extraRules', r); }}
                        placeholder="Ex: Parking réservé place 12..." />
                      <button onClick={() => { const r = [...(form.extraRules || [])]; r.splice(i, 1); update('extraRules', r); }}
                        style={{ padding: '8px 12px', background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, color: '#A32D2D', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 13, flexShrink: 0 }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => update('extraRules', [...(form.extraRules || []), ''])}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--white)', border: '1px dashed var(--border)', borderRadius: 10, fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)', width: 'fit-content' }}>
                    + Ajouter une règle
                  </button>
                </div>
              </div>

              {/* Génération IA */}
              <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#534AB7', marginBottom: 4 }}>Générer mon règlement avec l'IA</div>
                <textarea style={{ ...inputStyle, height: 60, resize: 'vertical', fontSize: 12, marginBottom: 10 }}
                  placeholder="Règles spécifiques à ajouter (optionnel)"
                  value={rulesExtra} onChange={e => setRulesExtra(e.target.value)}
                  onFocus={e => e.target.style.borderColor = '#534AB7'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <button onClick={generateRulesWithAI} disabled={rulesGenerating}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: rulesGenerating ? '#AFA9EC' : '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: rulesGenerating ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)' }}>
                  {rulesGenerating ? 'Génération...' : 'Générer mon règlement'}
                </button>
                {rulesError && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 8 }}>{rulesError}</div>}
              </div>
            </div>
          </div>
        )}

        {/* ═══ ETAPE Guide local ═══ */}
        {step === 5 + stepOffset && (
          <div style={sectionCard}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>Guide local — vos bons plans</h2>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8, lineHeight: 1.6 }}>Partagez vos adresses secrêtes avec vos voyageurs.</p>
            <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--green-mid)' }}>
              Les annonces avec un guide local reçoivent en moyenne 40% de demandes en plus
            </div>
            <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#534AB7', marginBottom: 4 }}>Générer mon guide local avec l'IA</div>
              <p style={{ fontSize: 12, color: '#534AB7', marginBottom: 10, lineHeight: 1.6 }}>
                L'IA va suggérer restaurants, plages, activités et transports autour de <strong>{form.city || 'votre ville'}</strong>.
              </p>
              <button onClick={generateGuideWithAI} disabled={guideGenerating || !form.city}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: guideGenerating ? '#AFA9EC' : !form.city ? '#ccc' : '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: (guideGenerating || !form.city) ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)' }}>
                {guideGenerating ? 'Recherche...' : form.city ? 'Générer le guide de ' + form.city : "Renseignez votre ville d'abord"}
              </button>
              {guideError && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 8 }}>{guideError}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { key: 'restaurants', label: 'Restaurants & Cafés',     placeholder: 'Ex: La Rossettisserie — meilleur poulet roti de Nice.' },
                { key: 'plages',      label: 'Plages & Nature',         placeholder: 'Ex: Plage de Passable — eau cristalline, moins touristique.' },
                { key: 'activités',   label: 'Activités & Visites',     placeholder: 'Ex: Marché du Cours Saleya tous les matins — arriver avant 9h.' },
                { key: 'transports',  label: 'Transports & Bons plans', placeholder: 'Ex: Bus 100 pour Monaco — 1,50 euros.' },
              ].map(field => (
                <div key={field.key}>
                  <label style={labelStyle}>{field.label}</label>
                  <textarea style={{ ...inputStyle, height: 80, resize: 'vertical', lineHeight: 1.6 }} placeholder={field.placeholder}
                    value={form.localGuide[field.key]} onChange={e => update('localGuide', { ...form.localGuide, [field.key]: e.target.value })}
                    onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ ETAPE Documents ═══ */}
        {step === 6 + stepOffset && !hasExistingListing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: 1 }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#3C3489', marginBottom: 3 }}>Vérification d'identité requise</div>
                <p style={{ fontSize: 12, color: '#534AB7', lineHeight: 1.6, margin: 0 }}>Havenly vérifie chaque hôte pour proteger les voyageurs.</p>
              </div>
            </div>
            {docsSubmitted ? (
              <div style={{ background: 'var(--white)', border: '1px solid var(--green)', borderRadius: 14, padding: '2rem', textAlign: 'center' }}>
                <h3 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 8 }}>Documents envoyes !</h3>
                <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7 }}>Vos documents sont en cours de verification. Continuez pour publier votre annonce.</p>
              </div>
            ) : (
              <>
                {docErrors.submit  && <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#A32D2D' }}>{docErrors.submit}</div>}
                {docErrors.general && <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#A32D2D' }}>{docErrors.general}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {DOCS_LIST.map(doc => (
                    <div key={doc.key} style={{ background: 'var(--white)', border: `1px solid ${docFiles[doc.key] ? 'var(--green)' : 'var(--border)'}`, borderRadius: 14, padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{doc.label}{doc.required ? <span style={{ color: '#E24B4A' }}>*</span> : <span style={{ fontSize: 11, background: '#FDF0E6', color: '#C4784A', padding: '2px 8px', borderRadius: 20, fontWeight: 500, marginLeft: 6 }}>Optionnel</span>}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{doc.sublabel}</div>
                        </div>
                        {docFiles[doc.key] && <span style={{ fontSize: 12, background: '#E8F5F0', color: '#0F6E56', padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>Ajoute</span>}
                      </div>
                      <div onDragOver={e => e.preventDefault()} onDrop={e => handleDocDrop(doc.key, e)} onClick={() => docRefs[doc.key].current.click()}
                        style={{ border: `2px dashed ${docFiles[doc.key] ? 'var(--green)' : 'var(--border-2)'}`, borderRadius: 10, padding: '18px', textAlign: 'center', cursor: 'pointer', background: docFiles[doc.key] ? 'var(--green-light)' : 'var(--surface)' }}>
                        {docPreviews[doc.key] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                            <img src={docPreviews[doc.key]} alt="" style={{ height: 50, borderRadius: 6, objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--green-mid)' }}>Document charge</div>
                              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>Cliquez pour remplacer</div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 22, marginBottom: 6 }}>+</div>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Glissez ou cliquez pour ajouter</div>
                            <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>JPG, PNG ou PDF — max 10 Mo</div>
                          </div>
                        )}
                      </div>
                      <input ref={docRefs[doc.key]} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => handleDocFile(doc.key, e.target.files[0])} />
                    </div>
                  ))}
                </div>
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Engagements obligatoires</div>
                  {legalModal && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setLegalModal(null)}>
                      <div style={{ background: 'var(--white)', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 15, fontWeight: 500 }}>{legalModal === 'cgu' ? 'CGU & Politique de confidentialite' : 'Charte de qualite Havenly'}</span>
                          <button onClick={() => setLegalModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-3)' }}>x</button>
                        </div>
                        <div style={{ overflowY: 'auto', padding: '20px', flex: 1, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.75 }}>
                          {legalModal === 'cgu' ? (
                            <><p><strong>Conditions générales d'Utilisation</strong></p><p style={{ marginTop: 10 }}>En utilisant Havenly, vous acceptez de fournir des informations exactes.</p></>
                          ) : (
                            <><p><strong>Charte de qualite Havenly</strong></p><p style={{ marginTop: 10 }}>Vous vous engagez a maintenir votre logement conforme, repondre sous 2h et respecter les voyageurs.</p></>
                          )}
                        </div>
                        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                          <button onClick={() => setLegalModal(null)} style={{ padding: '9px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Fermer</button>
                          <button onClick={() => { if (legalModal === 'cgu') setCguAccepted(true); else setCharteAccepted(true); setLegalModal(null); }} style={{ padding: '9px 20px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>J'accepte</button>
                        </div>
                      </div>
                    </div>
                  )}
                  <label style={{ display: 'flex', gap: 12, cursor: 'pointer', alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={cguAccepted} onChange={e => { setCguAccepted(e.target.checked); setDocErrors(prev => ({ ...prev, cgu: null })); }} style={{ accentColor: 'var(--green)', width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>J'accepte les <span onClick={e => { e.preventDefault(); setLegalModal('cgu'); }} style={{ color: 'var(--green-mid)', cursor: 'pointer', textDecoration: 'underline' }}>CGU</span> <span style={{ color: '#E24B4A' }}>*</span></div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Toutes les informations fournies sont exactes.</div>
                    </div>
                  </label>
                  {docErrors.cgu && <div style={{ fontSize: 12, color: '#A32D2D', marginLeft: 28 }}>{docErrors.cgu}</div>}
                  <div style={{ height: 1, background: 'var(--border)' }} />
                  <label style={{ display: 'flex', gap: 12, cursor: 'pointer', alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={charteAccepted} onChange={e => { setCharteAccepted(e.target.checked); setDocErrors(prev => ({ ...prev, charte: null })); }} style={{ accentColor: 'var(--green)', width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>J'accepte la <span onClick={e => { e.preventDefault(); setLegalModal('charte'); }} style={{ color: 'var(--green-mid)', cursor: 'pointer', textDecoration: 'underline' }}>Charte de qualite</span> <span style={{ color: '#E24B4A' }}>*</span></div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Je m'engage a maintenir mon logement conforme et repondre sous 2h.</div>
                    </div>
                  </label>
                  {docErrors.charte && <div style={{ fontSize: 12, color: '#A32D2D', marginLeft: 28 }}>{docErrors.charte}</div>}
                </div>
                <button onClick={handleDocsSubmit} disabled={docsUploading}
                  style={{ width: '100%', padding: '13px', background: docsUploading ? 'var(--border)' : '#534AB7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: docsUploading ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {docsUploading ? 'Envoi en cours...' : 'Soumettre mes documents'}
                </button>
                <p style={{ fontSize: 12, color: 'var(--ink-4)', textAlign: 'center', margin: 0 }}>Vous pouvez soumettre vos documents plus tard depuis votre dashboard.</p>
              </>
            )}
          </div>
        )}

        {/* ═══ ETAPE Recapitulatif ═══ */}
        {step === 7 + stepOffset && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={sectionCard}>
              <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Recapitulatif</h2>
              <div style={{ background: transparencyScore >= 80 ? 'var(--green-light)' : transparencyScore >= 50 ? '#EEEDFE' : '#FDF0E6', border: `1px solid ${transparencyScore >= 80 ? 'var(--green)' : transparencyScore >= 50 ? '#AFA9EC' : '#E8A87C'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: transparencyScore >= 80 ? 'var(--green-mid)' : transparencyScore >= 50 ? '#534AB7' : '#C4784A' }}>Score de transparence</span>
                  <span style={{ fontSize: 28, fontWeight: 500 }}>{transparencyScore}/100</span>
                </div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${transparencyScore}%`, height: '100%', background: transparencyScore >= 80 ? 'var(--green)' : transparencyScore >= 50 ? '#534AB7' : '#C4784A', borderRadius: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Titre',             val: form.title || '-' },
                  { label: 'Type',              val: form.type || '-' },
                  { label: 'catégorie',         val: form.category || '-' },
                  { label: 'Adresse',           val: form.address && form.city ? `${form.address}, ${form.city}` : '-' },
                  { label: 'GPS',               val: form.latitude ? `${form.latitude.toFixed(4)}, ${form.longitude.toFixed(4)}` : 'Non renseigne' },
                  { label: 'capacité',          val: `${totalGuests} voyageurs · ${form.beds} ch. · ${form.baths} sdb` },
                  { label: 'Prix / nuit',       val: form.priceNight ? `${form.priceNight} €` : '-' },
                  { label: 'Frais ménage',      val: form.cleaningFee ? `${form.cleaningFee} €` : '0 €' },
                  { label: 'Protection séjour', val: form.caution === 0 ? 'désactivée' : `${form.caution} €` },
                  { label: 'Check-in / out',    val: `${form.checkinTime} / ${form.checkoutTime}` },
                  { label: 'séjour minimum',    val: `${form.minNights} nuit${form.minNights > 1 ? 's' : ''}` },
                  { label: 'N° enregistrement', val: form.registrationNumber || '-' },
                  { label: 'Équipements',       val: allAmenities.length > 0 ? `${allAmenities.length} selectionnés` : '-' },
                  { label: 'Photos',            val: `${form.photos.filter(Boolean).length}/8 ajoutées` },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--ink-3)' }}>{row.label}</span>
                    <span style={{ color: 'var(--ink)', textAlign: 'right', maxWidth: '60%' }}>{row.val}</span>
                  </div>
                ))}
              </div>
            </div>
           
            <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#534AB7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#3C3489' }}>Générer mon annonce avec Emma IA</div>
                  <div style={{ fontSize: 12, color: '#534AB7' }}>Titres, description, FAQ, traductions EN/ES/DE</div>
                </div>
                <span style={{ marginLeft: 'auto', background: '#534AB7', color: '#fff', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>Exclusif Havenly</span>
              </div>
              <button onClick={() => { setEmmaModal(true); setEmmaResult(null); setEmmaTone(''); setEmmaGenerating(false); }}
                style={{ padding: '10px 20px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                Générer avec Emma
              </button>
            </div>
            <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--green-mid)', marginBottom: 8 }}>Avant publication</div>
              {['Votre annonce sera verifiee manuellement sous 24h', 'Email de confirmation a la publication', 'Pensez a déclarer votre activite a votre assureur', 'Emma sera activee automatiquement'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--green-mid)', marginBottom: 6 }}><span>+</span><span>{item}</span></div>
              ))}
            </div>
          </div>
        )}
{/* ── MODALE EMMA ── */}
        {emmaModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={() => { if (!emmaGenerating) setEmmaModal(false); }}>
            <div style={{ background: 'var(--white)', borderRadius: 16, maxWidth: 680, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ background: '#EEEDFE', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #AFA9EC', flexShrink: 0 }}>
                <div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 18, color: '#3C3489' }}>Emma — Générateur d'annonce</div>
                  <div style={{ fontSize: 12, color: '#534AB7', marginTop: 2 }}>Tout est pré-rempli depuis votre formulaire</div>
                </div>
                {!emmaGenerating && <button onClick={() => setEmmaModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#534AB7', lineHeight: 1 }}>×</button>}
              </div>
              <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
                {!emmaGenerating && !emmaResult && (
                  <div>
                    <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Informations détectées</div>
                      {[
                        { label: 'Type', val: form.type || '-' },
                        { label: 'Ville', val: form.city || '-' },
                        { label: 'capacité', val: `${totalGuests} voyageurs · ${form.beds} ch. · ${form.baths} sdb` },
                        { label: 'Proximité', val: form.proximity || 'Non renseignee' },
                        { label: 'Cible', val: form.target_audience || 'Non renseignee' },
                      ].map(r => (
                        <div key={r.label} style={{ display: 'flex', gap: 12, fontSize: 13, padding: '4px 0', borderBottom: '0.5px solid var(--border)' }}>
                          <span style={{ color: 'var(--ink-3)', width: 90, flexShrink: 0 }}>{r.label}</span>
                          <span style={{ color: 'var(--ink)' }}>{r.val}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 8 }}>
                        Ton souhaité pour la génération IA <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--ink-4)' }}>(optionnel)</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {[
  { key: 'Premium',    emoji: '✨', label: 'Premium' },
  { key: 'Vacances',   emoji: '🌴', label: 'Vacances' },
  { key: 'Familial',   emoji: '👨‍👩‍👧', label: 'Familial' },
  { key: 'Business',   emoji: '💼', label: 'Business' },
  { key: 'Relaxant',   emoji: '🧘', label: 'Relaxant' },
  { key: 'Romantique', emoji: '🌙', label: 'Romantique' },
].map(t => (
  <button key={t.key} onClick={() => setEmmaTone(emmaTone === t.key ? '' : t.key)}
    style={{ padding: '8px 16px', border: `1px solid ${emmaTone === t.key ? '#534AB7' : 'var(--border)'}`, borderRadius: 20, background: emmaTone === t.key ? '#EEEDFE' : 'var(--white)', color: emmaTone === t.key ? '#534AB7' : 'var(--ink-3)', fontSize: 13, fontWeight: emmaTone === t.key ? 500 : 400, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', gap: 6 }}>
    <span>{t.emoji}</span>{t.label}
  </button>
))}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>Utilisée uniquement pour la génération IA, non sauvegardée dans votre annonce</div>
                    </div>
                    <button onClick={async () => {
                      setEmmaGenerating(true);
                      setEmmaResult(null);
                      const amenityLabels = allAmenities.join(', ') || 'non renseignes';
                      const steps = ['Analyse du logement...', 'Génération des titres...', 'Redaction de la description...', 'Traductions EN/ES/DE...', 'Génération FAQ...'];
                      let si = 0;
                      const iv = setInterval(() => { setEmmaGenStep(steps[Math.min(si++, steps.length - 1)]); }, 900);
                      try {
                        const prompt = `Tu es Emma, redactrice experte en location courte Durée pour Havenly.

LOGEMENT :
- Type : ${form.type || 'Non precise'}
- Ville : ${form.city || 'Non precise'}
- Capacite : ${totalGuests} voyageurs, ${form.beds} chambres, ${form.baths} sdb
- Équipements : ${amenityLabels || 'non renseignes'}
- Photos du logement : ${form.photos.filter(Boolean).length} photos uploadees - analyse-les pour enrichir la description avec l'ambiance, la decoration, la luminosite, la vue
- Proximite : ${form.proximity || 'non renseignee'}
- Cible : ${form.target_audience || 'general'}
- Ton : ${emmaTone || 'chaleureux et professionnel'}
- Check-in : ${form.checkinTime} / Check-out : ${form.checkoutTime}
- Prix : ${form.priceNight ? form.priceNight + ' euros/nuit' : 'non renseigne'}
- Règles : ${form.rules || 'standard'}

INSTRUCTIONS TITRES :
- Maximum 60 caractères
- Mettre en avant le principal avantage (vue, plage, piscine, calme, design, centre-ville)
- Ton premium et chaleureux
- Donner envie de cliquer immediatement
- Jamais generique, jamais de MAJUSCULES abusives, pas plus d'1 emoji
- Exemples : "Villa avec piscine a 5 min des plages", "Loft design avec terrasse plein sud"

INSTRUCTIONS DESCRIPTION :
Structure :
1. Introduction emotionnelle (1-2 phrases qui donnent envie)
2. Points forts du logement
3. Experience voyageur (ce qu'ils vont vivre)
4. Localisation et acces
5. Conclusion accueillante
Règles : ton humain et professionnel, paragraphes courts, ne jamais inventer d'Équipements, donner envie de se projeter, lisible sur mobile

INSTRUCTIONS FAQ :
Priorite : horaires check-in/out, parking, WiFi, arrivee autonome, proximite, enfants, animaux

INSTRUCTIONS GUIDE LOCAL :
Restaurants, plages, activités, lieux connus. Ton local, chaleureux, touristique mais naturel.

INTERDITS :
- Ne jamais inventer des Équipements non mentionnes
- Ne jamais promettre des services inexistants
- Jamais de ton robotique ou publicitaire agressif
- Ne pas repeter plusieurs fois les memes mots

TON SELON STYLE :
${emmaTone === 'Premium' ? 'Vocabulaire raffine, elegance, exclusivite, experience haut de gamme' :
  emmaTone === 'Vacances' ? 'Joie, detente, evasion, soleil, liberte, souvenir' :
  emmaTone === 'Familial' ? 'Securite, confort, espace, enfants bienvenus, pratique' :
  emmaTone === 'Business' ? 'Efficacite, calme, connexion, productivite, confort professionnel' :
  emmaTone === 'Relaxant' ? 'Sérénité, calme, ressourcement, bien-être, nature' :
  emmaTone === 'Romantique' ? 'Intimite, charme, coucher de soleil, escapade a deux, cocooning' :
  'Chaleureux, accueillant, authentique, professionnel'}

Reponds UNIQUEMENT en JSON brut valide sans markdown:
{"titles":["titre1","titre2","titre3","titre4","titre5"],"accroche":"1 phrase emotionnelle","description":"250-300 mots structures en paragraphes","highlights":["point1","point2","point3","point4"],"arrival":"guide arrivee 3-4 phrases pratiques","faq":[{"q":"question1","a":"reponse1"},{"q":"question2","a":"reponse2"},{"q":"question3","a":"reponse3"},{"q":"question4","a":"reponse4"}],"desc_en":"full english description 80-100 words","desc_es":"descripcion completa espanol 80-100 palabras","desc_de":"vollstaendige deutsche Beschreibung 80-100 Woerter","suggestions":["amelioration concrete 1","amelioration concrete 2","amelioration concrete 3"]}`;
                        const res = await fetch('/api/emma', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ generate: true, prompt, max_tokens: 2000, images: form.photos.filter(Boolean).slice(0, 5) }) });
                        const data = await res.json();
                        clearInterval(iv);
                        const raw = data.text || '{}';
                        const clean = raw.replace(/```json|```/g, '').trim();
                        const parsed = JSON.parse(clean);
                        setEmmaResult(parsed);
                        if (parsed.titles?.[0]) { setEmmaChosenTitle(parsed.titles[0]); update('title', parsed.titles[0]); }
                        if (parsed.description) update('description', parsed.description);   // ← ajoute ici
                      } catch(e) {
                        clearInterval(iv);
                        setEmmaResult({ error: 'Erreur de génération. Réessayez.' });
                      } finally { setEmmaGenerating(false); }
                    }}
                      style={{ width: '100%', padding: '12px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      Lancer la génération
                    </button>
                  </div>
                )}
                {emmaGenerating && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 1rem', gap: 16 }}>
                    <div style={{ width: 40, height: 40, border: '3px solid #AFA9EC', borderTop: '3px solid #534AB7', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <div style={{ fontSize: 15, fontWeight: 500, color: '#3C3489' }}>Emma rédige votre annonce...</div>
                    <div style={{ fontSize: 13, color: '#534AB7' }}>{emmaGenStep}</div>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
                {emmaResult && !emmaGenerating && (
                  <div>
                    {emmaResult.error ? (
                      <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#A32D2D' }}>{emmaResult.error}</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>5 titres proposés — choisissez-en un</div>
                          {(emmaResult.titles || []).map((t, i) => (
                            <div key={i} onClick={() => { setEmmaChosenTitle(t); update('title', t); }}
                              style={{ padding: '10px 14px', border: `1px solid ${emmaChosenTitle === t ? '#534AB7' : 'var(--border)'}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: emmaChosenTitle === t ? '#EEEDFE' : 'var(--white)', color: emmaChosenTitle === t ? '#3C3489' : 'var(--ink)' }}>
                              <span>{t}</span>
                              {emmaChosenTitle === t && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                            </div>
                          ))}
                          <button onClick={async () => {
                            setEmmaGenerating(true);
                            const amenityLabels = allAmenities.join(', ') || 'non renseignes';
                            try {
                              const prompt = `Tu es Emma, redactrice experte en location courte Durée pour Havenly. Genere UNIQUEMENT 5 titres attractifs pour cette annonce. Type: ${form.type}, Ville: ${form.city}, Équipements: ${amenityLabels}, Proximite: ${form.proximity || 'non renseignee'}, Ton: ${emmaTone || 'chaleureux'}. Règles : max 60 caractères, premium et chaleureux, jamais generique, max 1 emoji. Reponds UNIQUEMENT en JSON brut : {"titles":["titre1","titre2","titre3","titre4","titre5"]}`;
                              const res = await fetch('/api/emma', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ generate: true, prompt, max_tokens: 500 }) });
                              const data = await res.json();
                              const clean = (data.text || '{}').replace(/```json|```/g, '').trim();
                              const parsed = JSON.parse(clean);
                              if (parsed.titles) setEmmaResult(prev => ({ ...prev, titles: parsed.titles }));
                            } catch(e) { console.error(e); }
                            finally { setEmmaGenerating(false); }
                          }}
                            style={{ fontSize: 11, padding: '3px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)', marginTop: 8 }}>
                            🔄 Regénérer les titres
                          </button>
                        </div>
                          {[
                          { label: 'Accroche courte', val: emmaResult.accroche, field: null },
                          { label: 'Description complète', val: emmaResult.description, field: 'description' },
                          { label: 'Guide d\'arrivee', val: emmaResult.arrival, field: null },
                        ].map(block => block.val && (
                          <div key={block.label} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                            <div style={{ padding: '8px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{block.label}</span>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
  <button onClick={() => navigator.clipboard.writeText(block.val)}
    style={{ fontSize: 11, padding: '3px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
    📋 Copier
  </button>
  <button onClick={async () => {
  setEmmaGenerating(true);
  const amenityLabels = allAmenities.join(', ') || 'non renseignes';
  try {
    const prompt = block.label === 'Accroche courte'
      ? `Genere UNIQUEMENT 1 phrase d'accroche emotionnelle et attractive pour cette annonce de location. Type: ${form.type}, Ville: ${form.city}, Ton: ${emmaTone || 'chaleureux'}. Reponds UNIQUEMENT en JSON brut : {"accroche":"ta phrase ici"}`
      : block.label === 'Description complete'
      ? `Genere UNIQUEMENT une description complete de 250-300 mots pour cette annonce. Type: ${form.type}, Ville: ${form.city}, Équipements: ${amenityLabels}, Proximite: ${form.proximity || 'non renseignee'}, Cible: ${form.target_audience || 'general'}, Ton: ${emmaTone || 'chaleureux'}. Structure: intro emotionnelle, points forts, experience voyageur, localisation, conclusion. Reponds UNIQUEMENT en JSON brut : {"description":"ta description ici"}`
      : `Genere UNIQUEMENT un guide d'arrivee pratique de 3-4 phrases pour cette annonce. Type: ${form.type}, Ville: ${form.city}, Check-in: ${form.checkinTime}, Check-out: ${form.checkoutTime}. Reponds UNIQUEMENT en JSON brut : {"arrival":"ton guide ici"}`;
    const res = await fetch('/api/emma', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ generate: true, prompt, max_tokens: 800 }) });
    const data = await res.json();
    const clean = (data.text || '{}').replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(clean);
    if (parsed.accroche) setEmmaResult(prev => ({ ...prev, accroche: parsed.accroche }));
    if (parsed.description) { setEmmaResult(prev => ({ ...prev, description: parsed.description })); update('description', parsed.description); }
    if (parsed.arrival) setEmmaResult(prev => ({ ...prev, arrival: parsed.arrival }));
  } catch(e) { console.error(e); }
  finally { setEmmaGenerating(false); }
}}
  style={{ fontSize: 11, padding: '3px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
  🔄 Regénérer
</button>
</div>
                            </div>
                            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--ink)', lineHeight: 1.7 }}>{block.val}</div>
                          </div>
                        ))}
                        {emmaResult.highlights?.length > 0 && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                            <div style={{ padding: '8px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Points forts</div>
                            <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {emmaResult.highlights.map((h, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'var(--green-light)', borderRadius: 20, fontSize: 12, color: 'var(--green-mid)', fontWeight: 500 }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                  {h}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {emmaResult.faq?.length > 0 && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                            <div style={{ padding: '8px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>FAQ voyageurs</div>
                            <div style={{ padding: '12px 14px' }}>
                              {emmaResult.faq.map((f, i) => (
                                <div key={i} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: i < emmaResult.faq.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{f.q}</div>
                                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{f.a}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                          <div style={{ padding: '8px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Traductions</div>
                          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[{ lang: 'EN', val: emmaResult.full_en }, { lang: 'ES', val: emmaResult.desc_es }, { lang: 'DE', val: emmaResult.desc_de }].filter(t => t.val).map(t => (
                              <div key={t.lang} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <span style={{ background: '#EEEDFE', color: '#534AB7', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, flexShrink: 0, marginTop: 2 }}>{t.lang}</span>
                                <span style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{t.val}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {emmaResult.suggestions?.length > 0 && (
                          <div style={{ background: '#FDF8F4', border: '1px solid #E8A87C', borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#C4784A', marginBottom: 8 }}>Suggestions d'amélioration</div>
                            {emmaResult.suggestions.map((s, i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#8B5A3A', marginBottom: 6 }}>
                                <span>→</span><span>{s}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <button onClick={() => { setEmmaResult(null); setEmmaGenerating(false); }}
                            style={{ padding: '9px 18px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', color: 'var(--ink-3)', background: 'var(--white)' }}>
                            Regenerer
                          </button>
                          <button onClick={() => {
                            if (emmaResult) {
                              const accroche = emmaResult.accroche ? emmaResult.accroche + '\n\n' : '';
                              const highlights = emmaResult.highlights?.length > 0
                                ? '\n\nPoints forts :\n' + emmaResult.highlights.map(h => '• ' + h).join('\n')
                                : '';
                              const fullDesc = accroche + (emmaResult.description || form.description) + highlights;
                              update('description', fullDesc);
                              if (emmaResult.arrival) {
                                const currentDesc = form.description || '';
                                update('description', currentDesc + '\n\n---\nGuide d\'arrivee :\n' + emmaResult.arrival);
                              }
                              if (emmaResult.faq) {
                                update('faq', emmaResult.faq);
                              }
                            }
                            setEmmaModal(false);
                          }}
                            style={{ padding: '9px 18px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                            Appliquer et fermer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ NAVIGATION ═══ */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 24, marginBottom: isMobile ? 60 : 0, gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', display: step === (isManager ? 1 : 0) && !isEdit ? 'none' : 'flex' }}>
  <button onClick={() => step > 0 ? setStep(step - 1) : onNavigate('dashboard')}
    style={{ padding: '11px 20px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
    {step === 0 ? 'Annuler' : 'Précédent'}
  </button>
  {!isEdit && (
    <button onClick={handleSaveDraft} disabled={draftSaving || !form.type || !form.city || videoUploading}
      style={{ padding: '11px 16px', background: 'transparent', color: draftSaved ? 'var(--green-mid)' : 'var(--ink-3)', border: `1px solid ${draftSaved ? 'var(--green)' : 'var(--border)'}`, borderRadius: 24, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 16 }}>{draftSaved ? '✅' : '💾'}</span>
      {isMobile ? (draftSaved ? 'Sauvegardé' : 'Brouillon') : (draftSaving ? 'Enregistrement...' : videoUploading ? 'Vidéo en cours...' : draftSaved ? 'Brouillon sauvegardé' : 'Enregistrer brouillon')}
    </button>
  )}
          {step < STEPS.length - 1 ? (
            <button onClick={() => {
              if (step === (isManager ? 3 : 2)) {
                const e = [];
                if (!form.title?.trim()) e.push('Titre de l\'annonce');
                if (!form.type) e.push('Type de logement');
                if (!form.city?.trim()) e.push('Ville');
                if (!form.address?.trim()) e.push('Adresse');
                if (!form.description || form.description.length < 100) e.push('Description (100 car. minimum)');
                if (e.length > 0) { setError(`Champs requis :\n- ${e.join('\n- ')}`); return; }
              }
              if (step === 3 + stepOffset) {
  const e = [];
  if (!form.priceNight || Number(form.priceNight) <= 0) e.push('Prix par nuit');
  if (form.countryCode === 'fr' && !form.communeNonSoumise) {
    const reg = (form.registrationNumber?.trim() || '').replace(/-/g, '').replace(/\s/g, '');
    if (!reg) {
      e.push('Numéro d\'enregistrement mairie obligatoire');
    } else if (!/^\d{13}$/.test(reg) || /^0+$/.test(reg)) {
      e.push('Format invalide — 13 chiffres requis. Ex: 0608800012342');
    }
  }
  if (e.length > 0) { setError(`Champs requis :\n- ${e.join('\n- ')}`); return; }
}
              setError(null);
              setStep(step + 1);
              if (step === 0) {
                setShowDraftTip(true);
                setTimeout(() => setShowDraftTip(false), 5000);
              }
            }} style={{ padding: '11px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
              Continuer
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!isEdit && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                    <input type="checkbox" checked={cguAccepted} onChange={e => setCguAccepted(e.target.checked)}
                      style={{ accentColor: 'var(--green)', marginTop: 2, flexShrink: 0, width: 15, height: 15 }} />
                    <span>J'accepte les <a href="/?page=legal&section=cgu" target="_blank" style={{ color: 'var(--green-mid)', textDecoration: 'underline' }}>Conditions Générales d'Utilisation</a> de Havenly</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                    <input type="checkbox" checked={charteAccepted} onChange={e => setCharteAccepted(e.target.checked)}
                      style={{ accentColor: 'var(--green)', marginTop: 2, flexShrink: 0, width: 15, height: 15 }} />
                    <span>J'accepte la <a href="/?page=legal&section=charte" target="_blank" style={{ color: 'var(--green-mid)', textDecoration: 'underline' }}>Charte Hôte Havenly</a> et m'engage à respecter les règles de la communauté</span>
                  </label>
                </div>
              )}
              <button onClick={handlePublish} disabled={loading || (!isEdit && (!cguAccepted || !charteAccepted))}
                style={{ padding: '11px 24px', background: loading || (!isEdit && (!cguAccepted || !charteAccepted)) ? 'var(--border)' : 'var(--ink)', color: loading || (!isEdit && (!cguAccepted || !charteAccepted)) ? 'var(--ink-4)' : '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: loading || (!isEdit && (!cguAccepted || !charteAccepted)) ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
                {loading ? 'Enregistrement...' : isEdit ? 'Enregistrer les modifications' : 'Publier mon annonce'}
              </button>
            </div>
          )}
        </div> {/* fin navigation */}
      </div> {/* fin colonne gauche */}
      {/* PREVIEW — desktop uniquement */}
      {!isMobile && !isEdit && (
        <ListingPreview
          form={form}
          photoPreviews={photoPreviews}
          customAmenities={customAmenities}
          transparencyScore={transparencyScore}
          videoUrl={videoUrl}
          step={step}
          stepOffset={stepOffset}
        />
      )}
    </div> {/* fin grid */}

    {showDraftTip && (
      <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--ink)', color: '#fff', padding: '12px 20px', borderRadius: 12, fontSize: 13, fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>💾</span>
        <span>Vous pouvez sauvegarder votre brouillon à tout moment !</span>
        <button onClick={() => setShowDraftTip(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, marginLeft: 8 }}>×</button>
      </div>
    )}

      {/* BOUTON FLOTTANT MOBILE */}
      {isMobile && !isEdit && (
        <button
          onClick={() => setShowMobilePreview(true)}
          style={{
            position: 'fixed', bottom: 80, right: 16, zIndex: 1000,
            background: 'var(--green)', color: '#fff',
            border: 'none', borderRadius: 28,
            padding: '10px 18px',
            fontSize: 13, fontWeight: 500,
            fontFamily: 'var(--sans)',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            cursor: 'pointer',
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          Aperçu
        </button>
      )}

      {/* DRAWER MOBILE */}
      {isMobile && !isEdit && showMobilePreview && (
        <div
          onClick={() => setShowMobilePreview(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,0.5)',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'var(--white)',
              borderRadius: '20px 20px 0 0',
              maxHeight: '85vh',
              overflowY: 'auto',
              paddingBottom: 32,
            }}>
            {/* Handle + header */}
            <div style={{
              position: 'sticky', top: 0, background: 'var(--white)',
              zIndex: 2, padding: '12px 16px 10px',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: 'var(--border-2)',
                margin: '0 auto 12px',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Aperçu voyageur</span>
                <button
                  onClick={() => setShowMobilePreview(false)}
                  style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>
                  ×
                </button>
              </div>
            </div>

            {/* Contenu — réutilise ListingPreview sans le sticky wrapper */}
            <div style={{ padding: '0 0 16px' }}>
              <ListingPreview
                form={form}
                photoPreviews={photoPreviews}
                customAmenities={customAmenities}
                transparencyScore={transparencyScore}
                videoUrl={videoUrl}
                step={step}
                stepOffset={stepOffset}
              />
            </div>
          </div>
        </div>
      )}

  </div>
  );
}
